import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { addDays, format } from 'date-fns';
import zipcode_to_timezone from 'zipcode-to-timezone';
import {
    useWarehouseAggregate,
    useCrossdockingSteps,
    STEPS,
    patchEvent,
    advance,
    genAttributes,
} from '@onward-delivery/core';
import styled from '@emotion/styled';
import {
    ThemeProvider,
    createTheme,
    Grid,
    Box,
    Typography,
    Button,
    MenuItem,
    Menu,
    CircularProgress,
    Snackbar,
    IconButton,
    Tooltip,
    Popover,
    TextField,
} from '@material-ui/core';
import { OnwardLink, PrimaryButton } from '@/styles/blocks';
import { ArrowDropDown } from '@material-ui/icons';
import WarningIcon from '@material-ui/icons/Warning';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { createOrderJob } from '@/components/ShipmentForm/queries/createOrderJob';
import { Lightbox } from 'react-modal-image';
import { UserContext } from '../App';
import * as Sentry from '@sentry/react';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { Alert, Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { UPSERT_ORDERS, UPSERT_ORDER_DYNAMIC } from '@/components/ShipmentForm/graphql/mutations';
import { FETCH_LOGOS } from '@/components/ShipmentForm/graphql/queries';
import { QUERY_BY_LEADER } from '@/graphql/queries/teammates';
import orderStatusStrings from '@/constants/orderStatusStrings';
import EditOrderModal from '@/components/ShipmentForm/modals/EditOrderModal';
import MarketplaceModal from '@/components/ShipmentForm/modals/MarketplaceModal';
import CreditCardModal from '@/components/ShipmentForm/modals/CreditCardModal';
import ExceptionResolutionModal from '@/components/admin/AdminExceptions/modals/ExceptionResolutionModal';
import { RESOLVE_EXCEPTION } from '@/components/admin/AdminExceptions/graphql';
import { asDateInTZ, asUTCDate } from '@/utilities/convertToISO';
import { withWarehouseEstimateEvent } from '@/utilities/warehouseEvents';
import { startCase } from 'lodash';
import OrderSummaryCard from './OrderSummaryCard';
import RatesTab from './RatesTab';
import ExceptionCard from './ExceptionCard';
import RoutedOrderCard from './RoutedOrderCard';
import PickUpCard from './PickUpCard';
import DropOffCard from './DropOffCard';
import { UPSERT_EVENT, INSERT_EXCEPTION, INSERT_EXCEPTION_EVENT, UPDATE_EXCEPTION } from './graphql/mutations';
import { MODALS } from './constants';
import { OnwardTabContainer, OnwardTab } from '../Tabs';
import { OnwardBreadcrumbActive, OnwardBreadcrumbInactive, OnwardBreadcrumbSpacer } from '../Breadcrumbs';
import StoreIcon from '@material-ui/icons/Store';

import { useExceptionCallbacks } from './hooks';
import OrderItems from './OrderItems/OrderItems';
import OrderImages from './OrderImages';
import CarrierReviewModal from '../CarrierReviewModal';
import CarrierEditBudget from './CarrierEditBudget';
import ExceptionModal from './modals/Exceptions';
import AssignDriverModal from './modals/AssignDriverModal';
import ConfirmationModal from './modals/ConfirmationModal';
import ChecklistModal from './modals/ChecklistModal';
import CrossdockPickupConfigModal from './modals/CrossdockPickupConfigModal';
import CrossdockConfigModal from './modals/CrossdockConfigModal';
import CommunicationLog from '../CommunicationLog';
import { OnwardCard } from './Card.jsx';
import { useClientUser, useOrderNotes } from '../../hooks';
import WarehousingCard from './WarehousingCard';
import Notes from './Notes/Notes';
import CrossdockingCard from './Crossdocking';
import CarrierInfoCard from './CarrierInfoCard';
import EmptyCard from './EmptyCard';

import { ORDER_BY_ID, ORDER_NOTES, LINKED_ORDERS } from './queries';
import { receivePalletVariables } from './utilities/receiveWrites';
import LinkedOrdersBreadcrumb from './LinkedOrdersBreadcrumb';
import OrderServicesTab from './ServicesTab';
import { useServiceCatalog } from '@/components/ShipmentForm/hooks/serviceCatalog';
import { customerHasServiceDefinitions } from '@/components/ShipmentForm/utilities/serviceCallback';
import { orderLinkedOrders } from './utilities/linkedOrders';
import { orderDetailsPath } from '@/constants/routes';
import { UPDATE_ORDER_STATUS, RELEASE_FROM_ROUTE } from './mutations';
import RescheduleOrderModal from './modals/RescheduleOrderModal';
import { buildReschedulePatch } from '@/components/Reschedule/utils';
import { rescheduleReasonLabel } from '@onward-delivery/core';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { useQuery, useMutation, useApolloClient, useLazyQuery } from '@apollo/client';
import AssignedToRouteErrorModal from './modals/AssignedToRouteErrorModal';
import BackToUnassignedModal from './modals/BackToUnassignedModal';
import { post } from '@/utilities/onwardClient';
import { CANCEL_LISTING, TRIGGER_ORDER_EVENT } from '@/constants/apiRoutes';
import { COMPLETION_SOURCES } from '@/constants/completionSources';
import { QUERY_BY_ORDER } from '@/graphql/queries/order_wh_events';
import AuctionModal from '../ShipmentForm/modals/ListingModal';
import useAction from '@/utilities/useQuery';
import { createListings } from '../ShipmentForm/queries/createListings';
import CreateIcon from '@mui/icons-material/Create';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@material-ui/icons/Replay';
import { inviteTempDriver } from './queries/inviteTempDriver';
import PalletDimensionsModal from './modals/PalletDimensionsModal';
import { toNational } from '@/utilities/formatPhoneNumber';
import CustomerAddressConfirmationCard from './CustomerAddressConfirmationCard';
import { CROSSDOCK_CONFIRM_TIMEFRAME_TEXT } from '@/constants/apiRoutes';
import { CONFIRMATION_SOURCE } from '@/constants/confirmationSource';
import { captureException } from '@sentry/react';
import MiddleMileCard from './MiddleMileCard';
import ReceiveModal from './modals/ReceiveModal';
import ScheduleModal from './modals/ScheduleModal';
import TimeframeModal from './modals/TimeframeModal';
import AssignModal from './modals/AssignModal';
import IntegrationPartnerCard from './IntegrationPartnerCard';
import { renderBolBlob } from './DocumentBOL';
import { computeOrderPermissions } from './permissions';
import UpdateItemPieceCountModal from './modals/UpdateItemPieceCountModal';
import WillCallCard from './WillCallCard';
import WHCrossdockCard from './WHCrossdockCard';
import { PARTNERSHIP_BY_CLIENTS } from '@/graphql/queries/partnerships';
import SurveyCard from './SurveyCard';
import CancelOrderModal from './CancelOrderModal';
import ActivityLog from '../ActivityLog';
import { InfoCircle } from 'react-bootstrap-icons';
import { buildReturnClone } from '@/utilities/orderClone';
import DriverTrackingModal from './modals/DriverTrackingModal';
import { trackDriver } from './queries/trackDriver';
import { useOrderCarrierPricing, useOrderShipperPricing, useReturnOrderFlow } from '@/hooks';
import CreateReturnModal from './modals/CreateReturnModal';
import { EXCEPTION_TYPES } from '@onward-delivery/core';
import MapModal from './modals/MapModal';
import { computeOrgClientIds } from '@/utilities/orgClientIds';
import { carrierDisplayName } from '@/utilities/carrierAlias';

const DAY = 1000 * 60 * 60 * 24;
const TODAY = new Date().setHours(0, 0, 0, 0);
const CUTOFF = new Date(TODAY - DAY * 365);

const useStyles = makeStyles({
    root: {
        minWidth: '15px',
    },
});

const SecondaryNavContainer = styled((props) => <Grid {...props} />)`
    padding: 0 40px;
    background: #fff;
    justify-content: space-between;
`;

const renderNotesCardIfExists = (notesTitle, notesBody) =>
    notesBody ? (
        <Grid container className="mt-3">
            <OnwardCard>
                <Typography variant="h6" color="secondary">
                    {notesTitle}
                </Typography>
                <Typography className="my-1" variant="body1">
                    {notesBody}
                </Typography>
            </OnwardCard>
        </Grid>
    ) : null;

function OrderDetailsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { cache } = useApolloClient();
    const { sidebarCollapsed, user, admin_clients } = useContext(UserContext);

    const {
        user_id,
        payment_type,
        stripe_payment_method,
        userType,
        accountType,
        teammateRoles,
        circles: _circles,
        locations,
        isImposter,
        isOnwardAdmin,
        isCorporateAdmin,
        isOnwardEngineer,
        shipper_integrations,
        isCarrierAdmin,
        partnerCarriersById,
    } = useClientUser();

    const { shipperIds: orgShipperIds, carrierIds: orgCarrierIds } = useMemo(() => {
        if (!isCorporateAdmin) return { shipperIds: null, carrierIds: null };
        return computeOrgClientIds(admin_clients);
    }, [isCorporateAdmin, admin_clients]);

    const source = location?.state?.source || '';
    const routeName = location?.state?.routeAlias || location?.state?.routeNumber || '';

    const { order_id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const modalParam = searchParams.get('modal');
    const [tabIndex, setTabIndex] = useState(0);
    const [viewImg, setViewImg] = useState('');
    const [carrierReviewOpen, setCarrierReviewOpen] = useState(false);
    const [carrierReview, setCarrierReview] = useState(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [crossdockPickupParams, setCrossdockPickupParams] = useState(null);
    const [crossdockConfigParams, setCrossdockConfigParams] = useState(null);
    const [receiveParams, setReceiveParams] = useState(null);
    const [scheduleParams, setScheduleParams] = useState(null);
    const [timeframeParams, setTimeframeParams] = useState(null);
    const [assignParams, setAssignParams] = useState(null);
    const [palletDimensionsParams, setPalletDimensionsParams] = useState(null);
    const [exceptionParams, setExceptionParams] = useState(null);
    const [resolveExceptionParams, setResolveExceptionParams] = useState({});
    const [confirmation, setConfirmation] = useState({});
    const [checklist, setChecklist] = useState({});
    const [driverTracking, setDriverTracking] = useState(null);
    const [itemLabelsUrl, setItemLabelsUrl] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [linkedAnchorEl, setLinkedAnchorEl] = useState(null);
    const [createReturnModalOpen, setCreateReturnModalOpen] = useState(false);
    const [assignedToRouteErrorModal, setAssignedToRouteErrorModal] = useState(false);
    const [notification, setNotification] = useState(null);
    const openMenu = Boolean(anchorEl);
    const linkedMenuOpen = Boolean(linkedAnchorEl);
    const [backToUnassignedModalOpen, setBackToUnassignedModalOpen] = useState(false);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [openListingModal, setOpenListingModal] = useState(false);
    const [pendingMarketPlace, setPendingMarketplace] = useState({
        flow: [],
        step: 0,
        order: null,
    });
    const [cancelOrderOnRouteModal, setCancelOrderOnRouteModal] = useState(null);
    const [editingItemPieceCount, setEditingItemPieceCount] = useState(modalParam === 'piece_count_verification');
    const [finePrint, setFinePrint] = useState(null);
    const [logo, setLogo] = useState(null);
    const [logoBackground, setLogoBackground] = useState(null);
    const [downloadAnchor, setDownloadAnchor] = useState(null);
    const [eventInput, setEventInput] = useState('');
    const [fetchLogos, { data: overrides }] = useLazyQuery(FETCH_LOGOS);
    const [mapParams, setMapParams] = useState({});

    const {
        loading: orderLoading,
        error: orderError,
        data: orderData,
        refetch: refetchOrder,
    } = useQuery(ORDER_BY_ID, {
        variables: {
            order_id: order_id,
        },
        onCompleted: (data) => {
            const _order = data?.orders?.[0];
            if (!_order) return;

            if (_order?.ratings?.length) {
                const relevantRating = _order.ratings
                    ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    ?.find((r) => r.reviewer_id === user_id);
                setCarrierReview(relevantRating);
            }
        },
        onError: (error) => {
            console.log(error);
            Sentry.captureException(error);
        },
    });

    const resolutionRemovedItems = useMemo(() => {
        return orderData?.orders?.[0]?.exceptions?.filter((mapping) => mapping?.item?.order_id === null);
    }, [orderData]);

    const order = useMemo(() => {
        return orderData?.orders?.[0] || {};
    }, [orderData]);

    // Services tab: shown when the shipper has service definitions (catalog) or the order already
    // carries services / is a service order. Edit/create/split inside are gated to carrier admins.
    const { serviceCatalog } = useServiceCatalog(order, { shipperId: order?.shipper_id });
    const showServicesTab = customerHasServiceDefinitions(order, serviceCatalog);

    const rootId = order?.original_order_id ?? order?.order_id;
    // Supplemental, additive UI (breadcrumb + dropdown) — intentionally NOT folded into the page
    // loading gate, so standalone orders don't pay an extra serial round-trip before rendering.
    const { data: linkedData } = useQuery(LINKED_ORDERS, {
        variables: { root_id: rootId },
        skip: !rootId,
    });
    const linkedOrders = useMemo(() => orderLinkedOrders(linkedData?.orders, rootId), [linkedData, rootId]);
    const hasLinkedOrders = linkedOrders.length > 1;
    const orderTitle = useMemo(
        () => [order.order_number, order.po_number, order.reference_id].filter((x) => !!x).join(' / '),
        [order.order_number, order.po_number, order.reference_id]
    );

    const shipperPricing = useOrderShipperPricing([order]);
    const carrierPricing = useOrderCarrierPricing([order]);

    const orderRate = useMemo(() => {
        if (!order) return '';
        if (order.oms) {
            return order.order_revenue ? `$${order.order_revenue.toFixed(2)}` : '-';
        } else if (!order.oms && user_id === order.shipper_id) {
            return shipperPricing;
        } else if (!order.oms && user_id === order.carrier_id) {
            return carrierPricing;
        } else {
            return '';
        }
    }, [order, user_id]);

    const surveyResponses = useMemo(() => {
        const responsesByType = (order?.survey_responses || []).reduce((acc, response) => {
            const type = response.survey_question.survey_type;
            return {
                ...acc,
                [type]: [...(acc[type] || []), response],
            };
        }, {});
        return {
            PREDELIVERY: [...(responsesByType.PREDELIVERY || []), ...(responsesByType.PREDELIVERY_DEFAULTS || [])],
            DRIVER: [...(responsesByType.DRIVER || []), ...(responsesByType.DRIVER_DEFAULTS || [])],
            POSTDELIVERY: [...(responsesByType.POSTDELIVERY || []), ...(responsesByType.POSTDELIVERY_DEFAULTS || [])],
        };
    }, [order]);

    const privacyExp = useMemo(() => {
        if (isOnwardAdmin) return {};
        return { _or: [{ private_to: { _is_null: true } }, { private_to: { _eq: startCase(accountType) } }] };
    }, [isOnwardAdmin, accountType]);

    const { loading: notesLoading, data: notesData } = useQuery(ORDER_NOTES, {
        variables: {
            order_id,
            notes_privacy: privacyExp,
        },
    });

    const notes = useMemo(() => {
        return notesData?.notes || [];
    }, [notesData]);

    // Memoized argument: useOrderNotes keys its memo on the array itself, so a literal here would
    // hand back a new notes object every render and retrigger anything depending on it.
    const orderList = useMemo(() => [order], [order]);
    const orderNotes = useOrderNotes(orderList, userType);

    const route = useMemo(() => {
        return (
            [...(order?.routes || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))?.[0]?.route || {}
        );
    }, [order]);

    const isFtlCrossdocked = useMemo(() => {
        return order.wh_events?.some((event) => event.action.includes('ADD_CD'));
    }, [order]);

    const createNewReturnOrder = (selectedItems = order.itemsByOrderId, clearPoNumber = false) =>
        buildReturnClone(order, { items: selectedItems, clearPoNumber });

    // Resolved here rather than inside DocumentBOL: that renders through @react-pdf
    // outside the React tree, where useClientUser has no provider to read.
    const bolCarrierName = carrierDisplayName(order?.order_carrier, partnerCarriersById);

    const bolInputs = useMemo(
        () => ({
            order,
            logo,
            logoBackground,
            finePrint,
            orderNotes,
            carrierName: bolCarrierName,
        }),
        [order, logo, logoBackground, finePrint, orderNotes, bolCarrierName]
    );
    // Each entry keyed by the inputs it was built from, so a stale or failed build is never offered
    // and reopening the popover retries only what is missing.
    const [bols, setBols] = useState({ mm: null, fm: null });
    const mmBolReady = bols.mm?.inputs === bolInputs && !!bols.mm?.url;
    const fmBolReady = bols.fm?.inputs === bolInputs && !!bols.fm?.url;

    // temporary variables until respective integration carriers are fully migrated
    const dispatchTrackCompatibilityVar = useMemo(() => {
        if (order?.dispatch_integration_type?.toLowerCase().includes('dispatchtrack')) {
            return 'DispatchTrack';
        } else return null;
    }, [order]);

    const xceleratorAxisCompatibilityVar = useMemo(() => {
        if (order?.dispatch_integration_type?.toLowerCase().includes('xcelerator')) {
            return 'Xcelerator';
        } else return null;
    }, [order]);

    // Stored URLs already carry a download token, so a presign failure can fall back to the raw
    // value. ref() throws synchronously on a bucket this storage instance does not own.
    useEffect(() => {
        const labels = typeof order?.item_labels === 'string' ? [order.item_labels] : order?.item_labels || [];

        // Every writer appends, so the newest label is last.
        const newestLabel = labels[labels.length - 1];
        const stored = typeof newestLabel === 'string' ? newestLabel : newestLabel?.url;
        if (!stored) {
            setItemLabelsUrl(null);
            return;
        }

        let cancelled = false;
        (async () => {
            let url = stored;
            try {
                url = await getDownloadURL(ref(getStorage(), stored.split('?')[0]));
            } catch (error) {
                Sentry.captureException(error);
            }

            if (!cancelled) setItemLabelsUrl(url);
        })();

        return () => {
            cancelled = true;
        };
    }, [order.item_labels]);

    // Both BOLs are only reachable through the download popover, and each lays out one PDF row
    // per item — eager generation costs that on every `order` identity change. Build on open, and
    // only when an input actually changed, so reopening the popover doesn't rebuild them.
    // `usePDF` is unusable for two documents: react-pdf broadcasts `change` through a module-level
    // listener list and starts the job synchronously, so feeding one instance renders the other
    // against whatever document it still holds. Awaiting one blob per document instead ties each to
    // the inputs it was built from, and matches how the rest of the app exports PDFs.
    useEffect(() => {
        if (!downloadAnchor || !order?.order_id) return;

        const needsMm = order.has_middle_mile_origin && bols.mm?.inputs !== bolInputs;
        const needsFm = bols.fm?.inputs !== bolInputs;
        if (!needsMm && !needsFm) return;

        let cancelled = false;
        const build = async (isMM, previous) => {
            try {
                const url = URL.createObjectURL(await renderBolBlob({ isMM, ...bolInputs }));
                if (previous) URL.revokeObjectURL(previous);
                return url;
            } catch (error) {
                Sentry.captureException(error);
                return null;
            }
        };

        (async () => {
            // Sequential: react-pdf keeps one module-level renderer, so parallel builds interleave.
            const mm = needsMm ? await build(true, bols.mm?.url) : null;
            const fm = needsFm ? await build(false, bols.fm?.url) : null;

            if (cancelled) {
                [mm, fm].forEach((url) => url && URL.revokeObjectURL(url));
                return;
            }

            // A failed build leaves the previous entry, whose stale key keeps it out of the popover.
            setBols((prev) => ({
                mm: mm ? { inputs: bolInputs, url: mm } : prev.mm,
                fm: fm ? { inputs: bolInputs, url: fm } : prev.fm,
            }));
        })();

        return () => {
            cancelled = true;
        };
    }, [downloadAnchor, order?.order_id, order?.has_middle_mile_origin, bolInputs, bols.mm, bols.fm]);

    const [upsertOrder, { loading: upsertLoading }] = useMutation(UPSERT_ORDERS, {
        update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'orders' });
        },
        onError: (e) => {
            console.error(e);
            Sentry.captureException(e);
            setNotification({ severity: 'error', message: 'Failed to save order. Please try again.' });
        },
    });

    const [upsertOrderDynamic, { loading: upsertDynamicLoading }] = useMutation(UPSERT_ORDER_DYNAMIC, {
        update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'orders' });
        },
        onError: (e) => {
            console.error(e);
            Sentry.captureException(e);
            setNotification({ severity: 'error', message: 'Failed to save order. Please try again.' });
        },
    });

    const [openEditOrderModal, setOpenEditOrderModal] = useState(false);

    const [handleSendBackToUnassigned] = useAction(async (body) => post(CANCEL_LISTING, body), {
        onComplete: ({ data }) => {
            if (data?.success) {
                setNotification({
                    severity: 'success',
                    message: 'Order sent back to unassigned.',
                });
            } else {
                setNotification({
                    severity: 'error',
                    message: data?.error || 'Error cancelling order.',
                });
            }
            setBackToUnassignedModalOpen(false);
        },
        onError: (err) => {
            console.error(err);
            Sentry.captureException(err);
            setNotification({
                severity: 'error',
                message: 'Error cancelling order. Please try again.',
            });
            setBackToUnassignedModalOpen(false);
        },
    });

    const [submitOrders, { loading: submitInflight }] = useAction(createOrderJob, {
        onError: (err) => {
            Sentry.captureException(err);
        },
    });

    const [resolve, { loading: resolveInflight }] = useMutation(RESOLVE_EXCEPTION, {
        update(cache, { data: { order, exception, items_removed } }) {
            cache.updateQuery(
                {
                    query: ORDER_BY_ID,
                    variables: {
                        order_id: order_id,
                    },
                },
                (data) => {
                    if (!order) {
                        return data;
                    }
                    const [updated] = order.returning;
                    const clone = {
                        ...data.orders[0],
                        ...updated,
                    };

                    return {
                        ...data,
                        orders: [clone],
                    };
                }
            );
        },
        onError: (err) => {
            Sentry.captureException(err);
        },
    });

    const [insertException, { loading: insertExceptionLoading }] = useMutation(INSERT_EXCEPTION, {
        onError: (error) => {
            Sentry.captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Failed to add exception. Please try again' });
        },
        update(cache, { data: { created } }) {
            cache.updateQuery(
                {
                    query: ORDER_BY_ID,
                    variables: {
                        order_id: order_id,
                    },
                },
                (data) => {
                    if (!order) {
                        return data;
                    }
                    const clone = {
                        ...data.orders[0],
                        exceptions: [...data.orders[0].exceptions, ...created.returning[0].mappings],
                    };

                    return {
                        ...data,
                        orders: [clone],
                    };
                }
            );
        },
    });
    const [updateException, { loading: updateExceptionLoading }] = useMutation(UPDATE_EXCEPTION, {
        onError: (error) => {
            Sentry.captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Failed to update exception. Please try again' });
        },
    });

    const [insertExceptionEvent, { loading: insertExceptionEventLoading }] = useMutation(INSERT_EXCEPTION_EVENT, {
        update: (cache, { data: { mappings, order } }) => {
            cache.updateQuery(
                {
                    query: ORDER_BY_ID,
                    variables: {
                        order_id: order_id,
                    },
                },
                (data) => {
                    if (!order) {
                        return data;
                    }

                    const clone = {
                        ...data.orders[0],
                        ...order,
                    };

                    return {
                        ...data,
                        orders: [clone],
                    };
                }
            );

            cache.updateQuery(
                {
                    query: QUERY_BY_ORDER,
                    variables: { order_id },
                },
                (data) => {
                    if (!order) {
                        return data;
                    }
                    const clone = [...order.wh_events];

                    return {
                        ...data,
                        results: clone,
                    };
                }
            );
        },
        onError: (error) => {
            Sentry.captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Failed to insert event. Please try again' });
        },
    });

    const saveAssociatedReturnOrder = useReturnOrderFlow({
        setReturnOrderId: (oid, rid) =>
            upsertOrderDynamic({
                variables: {
                    orders: [{ order_id: oid, return_order_id: rid }],
                    order_update_cols: ['return_order_id'],
                    items: [],
                    removals: [],
                    item_update_cols: [],
                },
            }),
    });

    const [upsertEvent, { loading: eventUpsertLoading }] = useMutation(UPSERT_EVENT, {
        update: (cache, { data: { events, order } }) => {
            refetchOrder();

            cache.updateQuery(
                {
                    query: QUERY_BY_ORDER,
                    variables: { order_id },
                },
                (data) => {
                    if (!events?.returning?.length) {
                        return data;
                    }

                    const clone = [...data.results];
                    for (const newEvent of events.returning) {
                        const idx = clone.findIndex((e) => e.event_id === newEvent.event_id);
                        if (idx > -1) {
                            clone.splice(idx, 1, newEvent);
                        } else {
                            clone.push(newEvent);
                        }
                    }

                    return {
                        ...data,
                        results: clone,
                    };
                }
            );
        },
        onError: (error) => {
            Sentry.captureException(error);
            console.error(error);
            setNotification({
                severity: 'error',
                message: 'Update failed. Order data may be stale, refresh the page and try again.',
            });
        },
    });

    const [findPartnership] = useLazyQuery(PARTNERSHIP_BY_CLIENTS, {
        onError: (error) => {
            console.error(error);
            Sentry.captureException(error);
        },
    });

    const [insertDriverTracking, { loading: isDriverTrackingLoading }] = useAction(trackDriver, {
        onComplete: () => {
            setNotification({
                severity: 'success',
                message: `Driver location updated`,
            });
            setDriverTracking(null);
        },
        onError: (error) => {
            console.error(error);
            setNotification({
                severity: 'error',
                message: error?.response?.data?.error || 'Error updating driver location',
            });
        },
    });

    const [sendWeblink, { loading: isLoadingWeblink }] = useAction(inviteTempDriver, {
        onComplete: () => {
            setNotification({
                severity: 'success',
                message: `Order ${order.order_number} weblink sent to driver`,
            });
        },
        onError: (error) => {
            console.error(error);
            setNotification({
                severity: 'error',
                message: error?.response?.data?.error || 'Error sending weblink',
            });
        },
    });

    const { data: warehouseEvents } = useQuery(QUERY_BY_ORDER, {
        variables: { order_id },
    });

    const warehouse = useWarehouseAggregate(warehouseEvents?.results);

    const crossdockingSteps = useCrossdockingSteps(warehouseEvents?.results || [], order?.order_type);
    const scheduledStepMeta = crossdockingSteps.findLast(([step]) => step === STEPS.SCHEDULED)?.[1];

    const { data: teammates } = useQuery(QUERY_BY_LEADER, {
        variables: { leader_id: user_id },
    });

    const drivers = useMemo(() => {
        return (teammates?.results || []).filter((teammate) => teammate?.roles?.driver);
    }, [teammates]);

    useEffect(() => {
        if (order) {
            fetchLogos({
                variables: {
                    shipper_id: order.shipper_id,
                    cutoff_ts: CUTOFF,
                },
            });
        }
    }, [order]);

    const [renewListing] = useAction(createListings, {
        onComplete: ({ data }) => {
            if (data?.success?.length) {
                setNotification({
                    severity: 'success',
                    message: 'Listing Renewed.',
                });
            } else {
                setNotification({
                    severity: 'error',
                    message: data?.error || 'Error renewing listing.',
                });
            }
            resetSendToMarketplace();
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'listings' });
            setOpenListingModal(false);
        },
        onError: (err) => {
            console.error(err);
            Sentry.captureException(err);
            setNotification({
                severity: 'error',
                message: 'Error renewing listing. Please try again.',
            });
            setOpenListingModal(false);
        },
    });

    const { createException } = useExceptionCallbacks(
        { order, exceptionParams, user_id },
        { insertExceptionEvent, insertException, updateException }
    );

    const onSaveOrder = ({
        errors,
        order: modified,
        items: itemsModified,
        itemsRemoved,
        itemUpdates,
        tags,
        tagsRemoved,
        documents,
        orderIds,
        subscriptions,
        services,
        servicesRemoved,
        references,
        referencesRemoved,
    }) => {
        if (errors?.hasMissingField?.length) {
            setNotification({
                severity: 'error',
                message: `Order has missing fields: ${errors.hasMissingField.join(', ')}.`,
            });
        } else if (errors?.phoneInvalid) {
            setNotification({ severity: 'error', message: 'Order has an invalid phone number.' });
        } else {
            if (errors?.failedPricing?.length) {
                let message = `Failed to calculate pricing for ${modified.order_number}.`;
                if (errors.failedPricing.includes('miles')) {
                    message = `${message} Cannot find route between pickup and dropoff locations.`;
                }
                if (errors.failedPricing.includes('delivery_type')) {
                    message = `${message} ${
                        modified.order_type === 'return' ? 'Return Pickup' : 'Delivery'
                    } type not set.`;
                }
                setNotification({ severity: 'warning', message });
            }

            const { notes, ...rest } = modified;

            upsertOrder({
                variables: {
                    orders: [
                        {
                            ...withWarehouseEstimateEvent(rest),
                            notes: {
                                data: notes,
                                on_conflict: {
                                    constraint: 'notes_pkey',
                                    update_columns: ['note'],
                                },
                            },
                        },
                    ],
                    items: itemsModified || [],
                    removals: itemsRemoved || [],
                    item_updates: itemUpdates || [],
                    tags: tags,
                    tag_removals: tagsRemoved,
                    order_ids: orderIds,
                    documents: documents || [],
                    email_subscriptions: subscriptions || [],
                    services: services || [],
                    service_removals: servicesRemoved || [],
                    references: references || [],
                    reference_removals: referencesRemoved || [],
                },
            });

            if (modified.delivery_date !== order.delivery_date && scheduledStepMeta) {
                const action =
                    scheduledStepMeta.after?.action ||
                    `${scheduledStepMeta.before?.transition?.next || 'START'}:PICKING_UP`;

                const toInsert = patchEvent({
                    curr: [{ order_id: order.order_id, action }],
                    prev: warehouseEvents?.results || [],
                    action,
                    patch: { est_delivery_date: modified.delivery_date },
                }).map(({ __typename, state, transition, driver, wh_driver, location, ...remaining }) => remaining);

                upsertEvent({
                    variables: {
                        events: toInsert,
                        order: { order_id: order.order_id },
                        // patchEvent folds the queried event in for its event_id; only the date this
                        // edit changed may be written back, or a since-patched column is reverted.
                        update_cols: ['est_delivery_date'],
                        order_update_cols: [],
                    },
                });
            }

            setOpenEditOrderModal(false);
        }
    };

    const handleSendingToMarketplace = ({ shipper_id, listings }) => {
        renewListing({
            shipper_id,
            listings,
        });
    };

    const [updateOrderStatus] = useMutation(UPDATE_ORDER_STATUS, {
        onError: (error) => {
            Sentry.captureException(error);
        },
    });

    const [releaseFromRoute, { loading: releaseFromRouteLoading }] = useMutation(RELEASE_FROM_ROUTE, {
        onError: (error) => {
            Sentry.captureException(error);
        },
    });

    const handleReschedule = ({ reason, notes, deliveryDate }) => {
        const reschedulePatch = buildReschedulePatch({ reason, notes });
        const activeRouteMappings = (order?.routes || []).filter(
            (m) => m?.mapping_id && (m?.duplicate_override == null || m?.duplicate_override === 'DISCARD')
        );

        const activeMappingIds = activeRouteMappings.map((m) => m.mapping_id);

        const optimizeRouteIds = activeRouteMappings
            .filter((m) => m?.route?.status !== 'inProgress')
            .map((m) => m.route_id)
            .filter(Boolean);

        const isCompleted = order.event_state === 'END';
        const isOutForDelivery = order.event_state === 'CD_COMPLETE';
        const isMM = (order.wh_events || []).some((e) => e.action === 'START:AWAITING');

        const events = [];
        if (isCompleted) {
            const reasonLabel = rescheduleReasonLabel(reason);
            const revertNotes = reschedulePatch.reschedule_reason_notes
                ? `${reasonLabel}: ${reschedulePatch.reschedule_reason_notes}`
                : reasonLabel;
            events.push({ action: 'END:REVERT', order_id: order.order_id, notes: revertNotes });
        }
        if (isOutForDelivery) {
            events.push({ action: 'CD_COMPLETE:ATTEMPT', order_id: order.order_id });
            events.push(
                ...advance(order, 'WAREHOUSE_RETURN', 'PICKED_UP', {}, { est_delivery_date: deliveryDate }).map(
                    ({ state, ...rest }) => rest
                )
            );
        } else {
            const scheduleAction = isMM ? 'RECEIVING:PICKING_UP' : 'START:PICKING_UP';
            const existingScheduleEvent = (warehouseEvents?.results || []).find((e) => e.action === scheduleAction);
            if (existingScheduleEvent) {
                events.push({
                    event_id: existingScheduleEvent.event_id,
                    order_id: order.order_id,
                    action: scheduleAction,
                    est_delivery_date: deliveryDate,
                });
            } else {
                events.push(
                    ...advance(
                        order,
                        isMM ? 'RECEIVING' : 'START',
                        'PICKED_UP',
                        {},
                        { est_delivery_date: deliveryDate }
                    ).map(({ state, ...rest }) => rest)
                );
            }
        }

        const orderUpdate = {
            order_id: order.order_id,
            delivery_date: deliveryDate,
            del_window_start: null,
            del_window_end: null,
            delivery_time_confirmed: null,
            confirmation_source: null,
            order_status: 'claimed',
            ltl_dropoff_arrival: null,
            completion_time: null,
            loading_completed: null,
            ltl_pickup_completed: null,
            ...reschedulePatch,
            ...(isCompleted ? { completion_source: null } : {}),
        };

        const orderUpdateCols = [
            'delivery_date',
            'del_window_start',
            'del_window_end',
            'delivery_time_confirmed',
            'confirmation_source',
            'order_status',
            'ltl_dropoff_arrival',
            'completion_time',
            'loading_completed',
            'ltl_pickup_completed',
            'reschedule_reason_code',
            'reschedule_reason_notes',
            ...(isCompleted ? ['completion_source'] : []),
        ];

        const upsert = upsertEvent({
            variables: {
                events,
                order: orderUpdate,
                update_cols: ['action', 'order_id', 'notes', 'est_delivery_date'],
                order_update_cols: orderUpdateCols,
            },
        });

        if (activeMappingIds.length > 0) {
            return upsert.then(() =>
                releaseFromRoute({ variables: { mapping_ids: activeMappingIds, route_ids: optimizeRouteIds } })
            );
        }

        return upsert;
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const resetSendToMarketplace = () => {
        setPendingMarketplace({
            flow: [],
            step: 0,
            order: null,
        });
    };

    const progressSendingToMarketplace = ({ order: optionalOrderModifier, listing: optionalListing }) => {
        if (pendingMarketPlace.step === pendingMarketPlace.flow.length - 1) {
            handleSendingToMarketplace({
                shipper_id: user_id,
                listings: [pendingMarketPlace.order.listing],
            });
        } else {
            setPendingMarketplace((prev) => {
                let next = {
                    ...prev,
                    step: prev.step + 1,
                };
                if (optionalListing) {
                    next.order = {
                        ...prev.order,
                        listing: {
                            ...optionalListing,
                        },
                    };
                } else if (optionalOrderModifier) {
                    next.order = {
                        ...optionalOrderModifier,
                    };
                }
                if (next.flow[next.step] === MODALS.EDIT_ORDER) {
                    setNotification({
                        severity: 'warning',
                        message: `Cannot submit to Onward, order is missing certain fields required for Onward Marketplace orders.`,
                    });
                }
                return next;
            });
        }
    };

    useEffect(() => {
        if (order?.carrier_id) {
            if (order?.oms) {
                findPartnership({
                    variables: {
                        shipperId: order.shipper_id,
                        carrierId: order.carrier_id,
                    },
                    onCompleted: ({ partnerships }) => {
                        if (partnerships[0]?.profile_img) {
                            setLogo(partnerships[0]?.profile_img);
                            setLogoBackground(partnerships[0]?.profile_img_color);
                        } else {
                            setLogo(order?.order_carrier?.white_label_img);
                            setLogoBackground(order?.order_carrier?.white_label_color);
                        }
                    },
                });
            }
        } else {
            setFinePrint(order?.order_shipper?.bol_fine_print);
            setLogo(order?.order_shipper?.white_label_img);
            setLogoBackground(order?.order_shipper?.white_label_color);
        }
    }, [order]);

    const [finalMileDriver, crossDockDriver] = useMemo(() => {
        if (order?.routes?.length > 0) {
            return [
                order.routes.find((mapping) => ['FULL', 'DROPOFF'].includes(mapping.type))?.route?.teammateByDriverId,
                order.routes.find((mapping) => mapping.type === 'PICKUP')?.route?.teammateByDriverId,
            ];
        } else {
            return [
                warehouse?.driver ||
                    (warehouse?.driver_name && warehouse?.driver_phone
                        ? { username: warehouse.driver_name, phone: warehouse.driver_phone }
                        : null),
                warehouse?.wh_driver ||
                    (warehouse?.wh_driver_name && warehouse?.wh_driver_phone
                        ? { username: warehouse.wh_driver_name, phone: warehouse.wh_driver_phone }
                        : null),
            ];
        }
    }, [order, warehouse]);

    const circles = useMemo(() => {
        if (isOnwardAdmin && !_circles) {
            //default use carrier circles if onward admin looking at order without being logged in as superadmin
            return order?.order_carrier?.user?.circles;
        }
        return _circles;
    }, [_circles, order]);

    // Order-level permissions (write, manifest, document access). See computeOrderPermissions for the matrix.
    const { editable, manifestEditable, documentsViewable } = useMemo(
        () =>
            computeOrderPermissions({
                order,
                user_id,
                userType,
                teammateRoles,
                accountType,
                isOnwardAdmin,
                isCorporateAdmin,
            }),
        [order, user_id, userType, teammateRoles, accountType, isOnwardAdmin, isCorporateAdmin]
    );

    const orderCancellable = useMemo(() => {
        // If the text field contains content in the cancelObject then the button still renders, disabled, with the text as the tooltip.

        let cancelObject = {
            canCancel: false,
            text: '',
            isOnRoute: false,
        };
        if (!editable && user_id !== order.carrier_id) return cancelObject;

        if (['cancelled', 'rejected', 'complete', 'routedComplete']?.includes(order.order_status)) {
            return cancelObject;
        }

        if (!route?.route_id) {
            cancelObject.canCancel = true;
            return cancelObject;
        }

        cancelObject.isOnRoute = true;

        if (route?.status === 'complete') {
            return cancelObject;
        }

        if (route?.status === 'inProgress') {
            cancelObject.text = `The route is in progress and cannot be unlocked. Please use the Route Tracking page to cancel the order or create an exception.`;
            return cancelObject;
        }

        // if route is active, then need to go to the plan page and unlock it
        if (['active', 'pending'].includes(route?.status)) {
            cancelObject.text = `This route is locked. Please view it on the Planning page and unlock the route in order to remove this order from it.`;
            return cancelObject;
        }

        // if route status is planning, then can just remove it, and do the proper functions to do so
        if (route?.status === 'planning') {
            cancelObject.canCancel = true;
            return cancelObject;
        }
    }, [editable, order, user_id, route]);

    const carrierCanCancel = useMemo(() => {
        return orderCancellable?.canCancel && !orderCancellable?.isOnRoute && order.carrier_id === user_id;
    }, [orderCancellable, order, user_id]);

    const warehouseEditable = useMemo(() => {
        const containsEvent = warehouseEvents?.results.find((e) => e.action === 'START:AWAITING');
        const received = warehouseEvents?.results.find((e) => e.transition?.next === 'RECEIVING');
        const omsShipper = editable && accountType !== 'carrier';
        const mktplaceShipper = order.warehouse_control_entity === 'SHIPPER' && order.shipper_id === user_id;

        return containsEvent && !received && (omsShipper || mktplaceShipper);
    }, [warehouseEvents, editable, accountType, order]);

    const handleSubmitStatus = async (status) => {
        const completion_time = status === 'complete' ? new Date().toISOString() : null;
        try {
            await updateOrderStatus({
                variables: {
                    order_id: order.order_id,
                    order_status: status,
                    completion_time: completion_time,
                    source: COMPLETION_SOURCES.WEB,
                },
            });
            handleClose();
        } catch (e) {
            Sentry.captureException(e);
            console.log(e);
        }
    };

    const overridesById = useMemo(() => {
        if (!overrides || overrides?.results?.length < 1) {
            return {};
        }

        return overrides.results.reduce((acc, override) => {
            acc[override.pricing_override_id] = override;

            return acc;
        }, {});
    }, [overrides]);

    const listing = useMemo(() => {
        if (pendingMarketPlace.order) {
            let orderTZ =
                zipcode_to_timezone.lookup(order?.dropoff_zip) || Intl.DateTimeFormat().resolvedOptions().timeZone;
            const utc = addDays(
                new Date(asUTCDate(new Date().toISOString()).setUTCHours(0, 0, 0, 0)),
                21
            ).toISOString();

            return {
                [pendingMarketPlace.order.order_id]: {
                    order_id: pendingMarketPlace.order.order_id,
                    listing_type: circles?.['bidding-disabled'] ? 'claim_now' : 'all',
                    shipper_rate: pendingMarketPlace.order.shipper_rate,
                    expiration_date: asDateInTZ(utc, orderTZ).toISOString(),
                },
            };
        }

        return {};
    }, [pendingMarketPlace]);

    let orderStatus = useMemo(() => {
        if (!Object.keys(order).length) return '';
        if (
            order.order_status === 'complete' &&
            (order.order_exception || order?.itemsByOrderId?.some((i) => i.item_exception))
        ) {
            return 'Complete with Exceptions';
        }
        return orderStatusStrings[order.order_status];
    }, [order]);
    const hasExceptions = useMemo(() => {
        return order.itemsByOrderId?.some(
            (item) => item.exceptions?.filter((mapping) => mapping.exception.status !== 'RESOLVED').length > 0
        );
    }, [order]);

    const itemExceptionGroups = useMemo(() => {
        const exceptionIdToGroup = {};

        (order?.itemsByOrderId || []).forEach((item) => {
            (item?.exceptions || []).forEach((mapping) => {
                const exceptionId = mapping?.exception?.exception_id;
                if (!exceptionIdToGroup[exceptionId]) {
                    exceptionIdToGroup[exceptionId] = { exception: mapping.exception, items: [] };
                }
                exceptionIdToGroup[exceptionId].items.push(item);
            });
        });

        (order?.exceptions || [])
            .filter(
                (mapping) => mapping?.exception_type === 'ITEM' && mapping?.item && mapping?.item?.order_id === null
            )
            .forEach((mapping) => {
                const exceptionId = mapping?.exception?.exception_id;
                if (!exceptionId) return;
                if (!exceptionIdToGroup[exceptionId]) {
                    exceptionIdToGroup[exceptionId] = { exception: mapping.exception, items: [] };
                }
                exceptionIdToGroup[exceptionId].items.push(mapping.item);
            });

        return Object.values(exceptionIdToGroup).map((group) => {
            const seenItemIds = new Set();
            group.items = group.items.filter((it) => {
                if (!it) return false;
                if (seenItemIds.has(it.item_id)) return false;
                seenItemIds.add(it.item_id);
                return true;
            });
            return group;
        });
    }, [order]);

    const mmIntegrationType = useMemo(() => {
        if (
            [order?.carrier_rate_override?.override_type, order?.shipper_rate_override?.override_type].includes('E2E')
        ) {
            return 'E2E';
        }

        if (order?.priced_as_middle_mile) {
            return 'MIDDLE_MILE';
        }

        return 'NONE';
    }, [order]);

    const OrderStatusStyles = {
        open: {
            color: '#fc8f00',
        },
        inProgress: {
            color: '#008BD9',
        },
        complete: {
            color: '#59b863',
        },
        pending: {
            color: '#fc8f00',
        },
        claimed: {
            color: '#fc8f00',
        },
        routedActive: {
            color: '#fc8f00',
        },
        routed: {
            color: '#fc8f00',
        },
        routedInProgress: {
            color: '#fc8f00',
        },
        routedComplete: {
            color: '#59b863',
        },
        cancelled: {
            color: '#D23E3E',
        },
        rejected: {
            color: '#D23E3E',
        },
        rescheduled: {
            color: colors.oranges.primary,
        },
        'Complete with Exceptions': {
            color: colors.oranges.primary,
        },
        on_hold: {
            color: colors.oranges.primary,
        },
        failed: {
            color: colors.reds.primary,
        },
    };

    const cardFrag = css`
        height: 100%;
        flex-grow: 1;
        display: flex;
    `;

    const theme = createTheme({
        typography: {
            fontFamily: ['Montserrat', 'Roboto', 'Arial'].join(','),
            h1: {
                fontWeight: 800,
                fontSize: '28px !important',
            },
            h3: {
                fontSize: 25,
                fontWeight: '800',
            },
            h4: {
                color: '#4c4c4c',
                fontWeight: '900',
                fontSize: 15,
            },
            h6: {
                color: '#8d8d8d',
                fontWeight: '800',
                fontSize: 18,
            },
            body1: {
                fontWeight: '650',
                fontSize: 16,
            },
            body2: {
                fontWeight: '500',
                fontSize: 16,
            },
            subtitle1: {
                color: '#8d8d8d',
                fontWeight: '500',
                fontSize: '10',
            },
            subtitle2: {
                color: `${
                    OrderStatusStyles[orderStatus] || OrderStatusStyles[order.order_status]
                        ? OrderStatusStyles[orderStatus]?.color || OrderStatusStyles[order.order_status]?.color
                        : 'black'
                }`,
                fontWeight: '700',
                fontSize: '10',
                border: '3px solid',
                borderRadius: '5px',
                paddingLeft: '4px',
                paddingRight: '4px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
            },
            button: {
                fontWeight: '550',
                textTransform: 'capitalize',
            },
        },
        palette: {
            text: {
                secondary: '#4c4c4c',
            },
            primary: {
                main: '#59b863',
                contrastText: '#fff',
            },
            secondary: {
                main: '#4c4c4c',
                contrastText: '#fff',
            },
        },
    });

    const handleTabChange = (event, newValue) => setTabIndex(newValue);
    const classes = useStyles();

    const backButtonBar = useMemo(() => {
        switch (source) {
            case 'order':
                return (
                    <React.Fragment>
                        <OnwardBreadcrumbInactive onClick={() => navigate(-1)}>Active</OnwardBreadcrumbInactive>
                        <OnwardBreadcrumbSpacer />
                        <OnwardBreadcrumbActive variant="body1" color="primary" className="ml-2">
                            Order {order.order_number}
                        </OnwardBreadcrumbActive>
                    </React.Fragment>
                );
            case 'route':
                return (
                    <React.Fragment>
                        <OnwardBreadcrumbInactive onClick={() => navigate(-2)}>Active</OnwardBreadcrumbInactive>
                        <OnwardBreadcrumbSpacer />
                        <OnwardBreadcrumbInactive onClick={() => navigate(-1)}>
                            Route {routeName}
                        </OnwardBreadcrumbInactive>
                        <OnwardBreadcrumbSpacer />
                        <OnwardBreadcrumbActive>Order {order.order_number}</OnwardBreadcrumbActive>
                    </React.Fragment>
                );
            case 'financials':
                return (
                    <React.Fragment>
                        <OnwardBreadcrumbInactive onClick={() => navigate(-1)}>Financials</OnwardBreadcrumbInactive>
                        <OnwardBreadcrumbSpacer />
                        <OnwardBreadcrumbActive variant="body1" color="primary" className="ml-2">
                            Order {order.order_number}
                        </OnwardBreadcrumbActive>
                    </React.Fragment>
                );
            default:
                if (linkedOrders.length > 0) {
                    return (
                        <LinkedOrdersBreadcrumb
                            orders={linkedOrders}
                            currentOrderId={order.order_id}
                            parentId={rootId}
                        />
                    );
                }
                return (
                    <React.Fragment>
                        <OnwardBreadcrumbInactive onClick={() => navigate(-1)}>My Orders</OnwardBreadcrumbInactive>
                        <OnwardBreadcrumbSpacer />
                        <OnwardBreadcrumbActive>Order {order.order_number}</OnwardBreadcrumbActive>
                    </React.Fragment>
                );
        }
    }, [source, linkedOrders, rootId, order.order_id, order.order_number, routeName]);

    const isOwnRoute = route.oms && (!route.carrier_id || route.carrier_id === user_id);
    const isOrderHandler = order?.carrier_id ? order?.carrier_id === user_id : order?.shipper_id === user_id;

    const showCancelButton = useMemo(() => {
        return (
            (editable &&
                (order.oms || order.shipper_id === user_id) &&
                (orderCancellable?.canCancel || orderCancellable?.text)) ||
            carrierCanCancel
        );
    }, [editable, order, orderCancellable, carrierCanCancel, user_id]);

    if (orderLoading) {
        return (
            <ThemeProvider theme={theme}>
                <Box className={`${sidebarCollapsed && 'collapse-margin'} sidenav-margin-responsive p-0 vh-100`}>
                    <Grid
                        container
                        xs={12}
                        className="ms-5 me-5 bg-white"
                        justifyContent="center"
                        alignContent="center"
                        css={css`
                            height: 100vh;
                            width: 100vw;
                        `}
                    >
                        <CircularProgress size="200px" color="primary" />
                    </Grid>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <>
            {/* edit order modal here instead of with other modals so themeprovider doesn't mess up styling */}
            {((!!pendingMarketPlace.order && pendingMarketPlace.flow[pendingMarketPlace.step] === MODALS.EDIT_ORDER) ||
                openEditOrderModal) && (
                <EditOrderModal
                    order={openEditOrderModal ? order : pendingMarketPlace.order}
                    open={
                        (!!pendingMarketPlace.order &&
                            pendingMarketPlace.flow[pendingMarketPlace.step] === MODALS.EDIT_ORDER) ||
                        openEditOrderModal
                    }
                    opt={{
                        readOnly: openEditOrderModal
                            ? !!order.carrier_invoice_id
                            : !!pendingMarketPlace.order.carrier_invoice_id,
                        enableSingleOrderManifest: true,
                        enableAdminTab: isCorporateAdmin,
                        adminTabShipperIds: orgShipperIds,
                        adminTabCarrierIds: orgCarrierIds,
                    }}
                    callbacks={
                        openEditOrderModal
                            ? {
                                  onSave: onSaveOrder,
                                  onClose: () => {
                                      setOpenEditOrderModal(false);
                                  },
                                  onError: (error) => {
                                      setNotification({
                                          severity: 'error',
                                          message: error?.message || error || `We're sorry, something went wrong.`,
                                      });
                                  },
                                  refetchOrder,
                              }
                            : {
                                  onSave: ({
                                      order,
                                      items: modified,
                                      itemsRemoved,
                                      itemUpdates,
                                      documents,
                                      subscriptions,
                                      services,
                                      servicesRemoved,
                                  }) => {
                                      upsertOrder({
                                          variables: {
                                              orders: [
                                                  {
                                                      ...order,
                                                      oms: true,
                                                  },
                                              ],
                                              items: modified || [],
                                              removals: itemsRemoved || [],
                                              item_updates: itemUpdates || [],
                                              documents: documents || [],
                                              email_subscriptions: subscriptions || [],
                                              services: services || [],
                                              service_removals: servicesRemoved || [],
                                          },
                                      }).then(
                                          () => {
                                              progressSendingToMarketplace({ order });
                                          },
                                          () => {
                                              setNotification({
                                                  severity: 'error',
                                                  message: 'Failed to modify order',
                                              });
                                              resetSendToMarketplace();
                                          }
                                      );
                                  },
                                  onClose: resetSendToMarketplace,
                                  onError: (error) => {
                                      Sentry.captureException(error);
                                      console.error(error);
                                      setNotification({
                                          severity: 'error',
                                          message: error?.message || error || `We're sorry, something went wrong.`,
                                      });
                                  },
                              }
                    }
                />
            )}
            <ThemeProvider theme={theme}>
                <Box
                    css={css`
                        height: 100%;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            height: 100%;
                            flex-wrap: nowrap;
                        `}
                    >
                        <SecondaryNavContainer container>
                            <Grid item sm={3} className="d-flex align-items-center">
                                {backButtonBar}
                            </Grid>

                            <Grid item sm={6}>
                                <Box display="flex" justifyContent="center" width="100%">
                                    <OnwardTabContainer
                                        value={tabIndex}
                                        onChange={handleTabChange}
                                        textColor="primary"
                                        indicatorColor="primary"
                                        variant={'scrollable'}
                                        scrollButtons={'auto'}
                                    >
                                        <OnwardTab
                                            label={
                                                <Grid direction="row" container>
                                                    {order.order_exception || hasExceptions ? (
                                                        <Grid
                                                            direction="column"
                                                            container
                                                            css={css`
                                                                flex-basis: 0;
                                                            `}
                                                        >
                                                            <WarningIcon
                                                                css={css`
                                                                    font-size: 20px;
                                                                    justify-content: center;

                                                                    margin-right: 8px;
                                                                `}
                                                            />
                                                        </Grid>
                                                    ) : null}
                                                    <Grid
                                                        direction="column"
                                                        container
                                                        css={css`
                                                            flex-basis: 0;
                                                        `}
                                                    >
                                                        Details
                                                    </Grid>
                                                </Grid>
                                            }
                                            value={0}
                                            classes={{
                                                root: classes.root,
                                            }}
                                            style={{ color: hasExceptions || order.order_exception ? 'orange' : null }}
                                        />
                                        <OnwardTab
                                            label={
                                                <Grid direction="row" container>
                                                    {hasExceptions ? (
                                                        <Grid
                                                            direction="column"
                                                            container
                                                            css={css`
                                                                flex-basis: 0;
                                                            `}
                                                        >
                                                            <WarningIcon
                                                                css={css`
                                                                    font-size: 20px;
                                                                    justify-content: center;

                                                                    margin-right: 8px;
                                                                `}
                                                            />
                                                        </Grid>
                                                    ) : null}
                                                    <Grid
                                                        direction="column"
                                                        container
                                                        css={css`
                                                            flex-basis: 0;
                                                        `}
                                                    >
                                                        Items
                                                    </Grid>
                                                </Grid>
                                            }
                                            value={1}
                                            style={{ color: hasExceptions ? 'orange' : null }}
                                            classes={{
                                                root: classes.root,
                                            }}
                                        />
                                        {showServicesTab && (
                                            <OnwardTab
                                                label="Services"
                                                value={7}
                                                classes={{
                                                    root: classes.root,
                                                }}
                                            />
                                        )}
                                        <OnwardTab
                                            label="POD/Images"
                                            value={2}
                                            classes={{
                                                root: classes.root,
                                            }}
                                        />
                                        <OnwardTab
                                            label="Communications Log"
                                            value={3}
                                            classes={{
                                                root: classes.root,
                                            }}
                                        />
                                        <OnwardTab
                                            label="Notes"
                                            value={4}
                                            classes={{
                                                root: classes.root,
                                            }}
                                        />
                                        {(user?.roles?.['ADMIN'] ||
                                            user?.roles?.['ONWARD_ADMIN'] ||
                                            teammateRoles?.admin) && (
                                            <OnwardTab
                                                label="Rates"
                                                value={5}
                                                classes={{
                                                    root: classes.root,
                                                }}
                                            />
                                        )}
                                        <OnwardTab
                                            label="Activity Log"
                                            value={6}
                                            classes={{
                                                root: classes.root,
                                            }}
                                        />
                                    </OnwardTabContainer>
                                </Box>
                            </Grid>
                            <Grid item sm={3} className="d-flex align-items-center justify-content-end">
                                {documentsViewable && (
                                    <>
                                        <Tooltip title="Download Documents">
                                            <span>
                                                <IconButton onClick={(e) => setDownloadAnchor(e.currentTarget)}>
                                                    <FileDownloadIcon
                                                        css={css`
                                                            color: ${colors.greens.primary};
                                                        `}
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Popover
                                            open={Boolean(downloadAnchor)}
                                            anchorEl={downloadAnchor}
                                            onClose={() => setDownloadAnchor(null)}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'right',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'right',
                                            }}
                                        >
                                            <Grid container direction="column">
                                                {itemLabelsUrl ? (
                                                    <OnwardLink
                                                        target="_blank"
                                                        href={itemLabelsUrl}
                                                        css={css`
                                                            margin: 12px;
                                                        `}
                                                    >
                                                        Download Order Item Labels
                                                    </OnwardLink>
                                                ) : null}
                                                {order.has_middle_mile_origin ? (
                                                    <OnwardLink
                                                        disabled={!mmBolReady}
                                                        target="_blank"
                                                        href={mmBolReady ? bols.mm.url : undefined}
                                                        download={`OnwardMiddleMileBOL_${order.order_number}.pdf`}
                                                        css={css`
                                                            margin: 12px;
                                                        `}
                                                    >
                                                        {mmBolReady
                                                            ? 'Download middle mile BOL'
                                                            : 'Generating middle mile BOL...'}
                                                    </OnwardLink>
                                                ) : null}
                                                <OnwardLink
                                                    disabled={!fmBolReady}
                                                    target="_blank"
                                                    href={fmBolReady ? bols.fm.url : undefined}
                                                    download={`OnwardBOL_${order.order_number}.pdf`}
                                                    css={css`
                                                        margin: 12px;
                                                    `}
                                                >
                                                    {fmBolReady
                                                        ? 'Download final mile BOL'
                                                        : 'Generating final mile BOL...'}
                                                </OnwardLink>
                                            </Grid>
                                        </Popover>
                                    </>
                                )}
                                {editable ? (
                                    <Tooltip title="Reschedule Order">
                                        <span>
                                            <IconButton onClick={() => setRescheduleOpen(true)}>
                                                <EventRepeatIcon style={{ color: '#59b863' }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                ) : null}
                                {editable ? (
                                    <Tooltip title="Edit Order">
                                        <span>
                                            <IconButton onClick={() => setOpenEditOrderModal(true)}>
                                                <CreateIcon style={{ color: '#59b863' }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                ) : null}
                                {showCancelButton && (
                                    <Tooltip title={`${orderCancellable?.text}`}>
                                        <span>
                                            <IconButton
                                                disabled={!orderCancellable?.canCancel}
                                                onClick={() => setCancelOrderOnRouteModal(true)}
                                            >
                                                <CancelIcon
                                                    style={{
                                                        color: `${
                                                            orderCancellable?.canCancel
                                                                ? colors.reds.primary
                                                                : colors.greys.primary
                                                        }`,
                                                    }}
                                                />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                {order.shipper_id === user_id &&
                                    (order.order_status === 'open' || order.order_status === 'pending') &&
                                    !order.oms &&
                                    order.routes?.length === 0 &&
                                    !order.is_middle_mile && (
                                        <Tooltip title="Send Back to Unassigned">
                                            <span>
                                                <IconButton onClick={() => setBackToUnassignedModalOpen(true)}>
                                                    <ReplayIcon style={{ color: '#59b863' }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                            </Grid>
                            <AssignDriverModal
                                setOpenModal={setAssignModalOpen}
                                isOpen={assignModalOpen}
                                onClose={() => setAssignModalOpen(false)}
                                drivers={drivers}
                                order={order}
                            />
                            <BackToUnassignedModal
                                isOpen={backToUnassignedModalOpen}
                                onClose={() => setBackToUnassignedModalOpen(false)}
                                handleSubmit={() =>
                                    handleSendBackToUnassigned({ listing_id: order?.listing.listing_id })
                                }
                            />
                            <CreditCardModal
                                handleClose={resetSendToMarketplace}
                                onSuccess={() => {
                                    progressSendingToMarketplace({});
                                }}
                                open={pendingMarketPlace.flow[pendingMarketPlace.step] === MODALS.CREDIT_CARD}
                            />
                            <MarketplaceModal
                                open={pendingMarketPlace.flow[pendingMarketPlace.step] === MODALS.MARKETPLACE_TOC}
                                onClose={resetSendToMarketplace}
                                onSubmit={() => {
                                    progressSendingToMarketplace({});
                                }}
                                orders={
                                    pendingMarketPlace.order
                                        ? { [pendingMarketPlace.order.order_id]: pendingMarketPlace.order }
                                        : {}
                                }
                                listings={
                                    pendingMarketPlace?.order?.listing
                                        ? {
                                              [pendingMarketPlace.order.order_id]: pendingMarketPlace.order.listing,
                                          }
                                        : listing
                                }
                            />
                            <AuctionModal
                                open={
                                    openListingModal ||
                                    (!!pendingMarketPlace.order &&
                                        pendingMarketPlace.flow[pendingMarketPlace.step] === MODALS.AUCTION_DETAILS)
                                }
                                order={pendingMarketPlace.order || order}
                                listing={listing?.[order.order_id] || {}}
                                callbacks={{
                                    onClose: () => {
                                        setOpenListingModal(false);
                                        resetSendToMarketplace();
                                    },
                                    onSave: (val) => {
                                        if (pendingMarketPlace.order) {
                                            progressSendingToMarketplace({ listing: val.listing });
                                        } else {
                                            handleSendingToMarketplace({
                                                shipper_id: user_id,
                                                listings: [val.listing],
                                            });
                                        }
                                    },
                                }}
                            />
                            <ReceiveModal
                                order={order}
                                {...(receiveParams || {})}
                                callbacks={{
                                    onClose: () => {
                                        setReceiveParams(null);
                                    },
                                    onSubmit: ({ events, cols }) => {
                                        upsertEvent({
                                            variables: {
                                                events: !!events ? events : [],
                                                order: { order_id },
                                                update_cols: !!cols ? cols : [],
                                                order_update_cols: [],
                                                ...receivePalletVariables({
                                                    events,
                                                    params: receiveParams,
                                                    order,
                                                    circles,
                                                }),
                                            },
                                        }).then(() => {
                                            setReceiveParams(null);
                                        });
                                    },
                                }}
                            />
                            <ScheduleModal
                                order={order}
                                {...(scheduleParams || {})}
                                callbacks={{
                                    onClose: () => {
                                        setScheduleParams(null);
                                    },
                                    onSubmit: ({ event, order: update }) => {
                                        upsertEvent({
                                            variables: {
                                                events: !!event ? [{ ...event, order_id: order.order_id }] : [],
                                                order: { ...update, order_id: order.order_id },
                                                update_cols: !!event ? Object.keys(event) : [],
                                                order_update_cols: Object.keys(update),
                                            },
                                        }).then(() => {
                                            setScheduleParams(null);
                                        });
                                    },
                                }}
                            />
                            <TimeframeModal
                                order={order}
                                {...(timeframeParams || {})}
                                callbacks={{
                                    onClose: () => {
                                        setTimeframeParams(null);
                                    },
                                    onSubmit: ({ event, order: update, sendText }) => {
                                        upsertEvent({
                                            variables: {
                                                events: !!event ? [{ ...event, order_id: order.order_id }] : [],
                                                order: { ...update, order_id: order.order_id },
                                                update_cols: !!event ? Object.keys(event) : [],
                                                order_update_cols: Object.keys(update),
                                            },
                                        }).then((result) => {
                                            if (sendText && result?.data?.order) {
                                                post(CROSSDOCK_CONFIRM_TIMEFRAME_TEXT, {
                                                    orderId: order.order_id,
                                                })
                                                    .then(() => {
                                                        setTimeframeParams(null);
                                                        setNotification({
                                                            severity: 'success',
                                                            message: 'Customer texted, awaiting confirmation',
                                                        });
                                                    })
                                                    .catch((err) => {
                                                        captureException(err);
                                                        setNotification({
                                                            severity: 'error',
                                                            message:
                                                                'Failed to send customer timeframe confirmation text',
                                                        });
                                                    });
                                            } else {
                                                setTimeframeParams(null);
                                            }
                                        });
                                    },
                                }}
                            />
                            <AssignModal
                                order={order}
                                {...(assignParams || {})}
                                drivers={drivers}
                                callbacks={{
                                    onClose: () => {
                                        setAssignParams(null);
                                    },
                                    onSubmit: ({ event, order: update }) => {
                                        upsertEvent({
                                            variables: {
                                                events: !!event ? [{ ...event, order_id: order.order_id }] : [],
                                                order: { ...update, order_id: order.order_id },
                                                update_cols: !!event ? ['order_id', 'driver_id', 'action'] : [],
                                                order_update_cols: Object.keys(update),
                                            },
                                        }).then(() => {
                                            setAssignParams(null);
                                        });
                                    },
                                }}
                            />
                            <CrossdockPickupConfigModal
                                order={order}
                                drivers={drivers}
                                {...(crossdockPickupParams || {})}
                                callbacks={{
                                    onClose: () => {
                                        setCrossdockPickupParams(null);
                                    },
                                    onSubmit: ({ event, order: update, sendConfirm }) => {
                                        upsertEvent({
                                            variables: {
                                                events: !!event ? [{ ...event, order_id: order.order_id }] : [],
                                                order: { ...update, order_id: order.order_id },
                                                update_cols: !!event
                                                    ? Object.keys(event).filter(
                                                          (col) => !['driver', 'wh_driver'].includes(col)
                                                      )
                                                    : [],
                                                order_update_cols: Object.keys(update),
                                            },
                                        }).then(({ data }) => {
                                            if (data?.event && sendConfirm) {
                                                post(CROSSDOCK_CONFIRM_TIMEFRAME_TEXT, {
                                                    orderId: order.order_id,
                                                }).catch((err) => {
                                                    captureException(err);
                                                    setNotification({
                                                        severity: 'error',
                                                        message: 'Failed to send confirmation text.',
                                                    });
                                                });
                                            }
                                        });
                                        setCrossdockPickupParams(null);
                                    },
                                }}
                            />
                            <CrossdockConfigModal
                                order={order}
                                drivers={drivers}
                                warehouses={locations}
                                {...(crossdockConfigParams || {})}
                                callbacks={{
                                    onClose: () => {
                                        setCrossdockConfigParams(null);
                                    },
                                    onSubmit: ({ event, order: update, sendConfirm }) => {
                                        upsertEvent({
                                            variables: {
                                                events: !!event ? [{ ...event, order_id: order.order_id }] : [],
                                                order: { ...update, order_id: order.order_id },
                                                update_cols: !!event
                                                    ? Object.keys(event).filter(
                                                          (col) => !['driver', 'wh_driver'].includes(col)
                                                      )
                                                    : [],
                                                order_update_cols: Object.keys(update),
                                            },
                                        }).then(({ data }) => {
                                            if (data?.event && sendConfirm) {
                                                post(CROSSDOCK_CONFIRM_TIMEFRAME_TEXT, {
                                                    orderId: order.order_id,
                                                }).catch((err) => {
                                                    captureException(err);
                                                    setNotification({
                                                        severity: 'error',
                                                        message: 'Failed to send confirmation text.',
                                                    });
                                                });
                                            }
                                        });
                                        setCrossdockConfigParams(null);
                                    },
                                }}
                            />
                            <PalletDimensionsModal
                                order={order}
                                open={!!palletDimensionsParams}
                                callbacks={{
                                    onClose: () => {
                                        setPalletDimensionsParams(null);
                                    },

                                    onSubmit: ({ order: update }) => {
                                        upsertOrderDynamic({
                                            variables: {
                                                orders: [
                                                    {
                                                        order_id: order.order_id,
                                                        ...update,
                                                    },
                                                ],
                                                order_update_cols: Object.keys(update),
                                                items: [],
                                                removals: [],
                                                item_update_cols: [],
                                            },
                                        }).then(() => {
                                            setPalletDimensionsParams(null);
                                        });
                                    },
                                }}
                            />
                            <ConfirmationModal
                                open={!!confirmation.content}
                                content={confirmation.content}
                                callbacks={{
                                    onClose: () => {
                                        setConfirmation({});
                                    },
                                    onConfirm: () => confirmation.callback().then(() => setConfirmation({})),
                                }}
                            />
                            <ChecklistModal
                                open={!!checklist.content}
                                order={order}
                                {...(checklist || {})}
                                items={order.itemsByOrderId}
                                callbacks={{
                                    onClose: () => {
                                        setChecklist({});
                                    },
                                    onConfirm: (...params) =>
                                        checklist.callback(...params).then(() => setChecklist({})),
                                }}
                            />
                            <DriverTrackingModal
                                open={!!driverTracking}
                                driver={driverTracking}
                                order={order}
                                callbacks={{
                                    onClose: () => {
                                        setDriverTracking(null);
                                    },
                                    onConfirm: ({ driver, location }) => {
                                        insertDriverTracking({
                                            driver: {
                                                entity_id: order.order_id,
                                                key: driver.key || driver.phone,
                                                entity_type: 'ORDER',
                                                name: driver.name || 'Anonymous Driver',
                                                phone: driver.phone,
                                                email: driver.email || driver.name || 'Anonymous Driver',
                                            },
                                            long: location.lng,
                                            lat: location.lat,
                                            timestamp: new Date().toISOString(),
                                        });
                                    },
                                }}
                            />
                            <UpdateItemPieceCountModal
                                items={order?.itemsByOrderId || []}
                                open={!!editingItemPieceCount}
                                onClose={() => setEditingItemPieceCount(false)}
                                order={order}
                            />
                            <ExceptionResolutionModal
                                {...resolveExceptionParams}
                                loading={resolveInflight || submitInflight}
                                callbacks={{
                                    onClose: () => {
                                        setResolveExceptionParams({});
                                    },
                                    onResolve: ({ toSubmit, items = [], order = {}, exception = {}, event = {} }) => {
                                        const toRemove = items.map((item) => item.item_id);
                                        const { order_id, ...orderUpdates } = order;
                                        const { event_id, ...eventUpdates } = event;
                                        const { exception_id, type, ...exceptionUpdates } = exception;
                                        if (
                                            [
                                                EXCEPTION_TYPES.SHORTAGE,
                                                EXCEPTION_TYPES.DAMAGED,
                                                'itemsTooDamaged',
                                            ].includes(type) &&
                                            order.order_status === 'on_hold'
                                        ) {
                                            orderUpdates.order_status = order?.oms ? 'claimed' : 'open';
                                        }

                                        return Promise.all([
                                            resolve({
                                                variables: {
                                                    orders: order_id ? [order_id] : [],
                                                    events: event_id ? [event_id] : [],
                                                    exceptions: exception_id ? [exception_id] : [],
                                                    items: toRemove,
                                                    order_update: orderUpdates,
                                                    event_update: eventUpdates,
                                                    exception_update: exceptionUpdates,
                                                    include_route_update: false,
                                                },
                                            }),
                                            ...(toSubmit
                                                ? [
                                                      submitOrders({
                                                          client_id: toSubmit.shipper_id,
                                                          orders: [toSubmit],
                                                          filename: undefined,
                                                          type: 'MANUAL',
                                                      }),
                                                  ]
                                                : []),
                                        ]).then(async ([, resp]) => {
                                            if (resp?.data?.jobs?.length > 0) {
                                                if (exception.resolution === 'CREATE_RETURN') {
                                                    await saveAssociatedReturnOrder(order_id, resp.data.jobs[0]);
                                                }
                                                navigate(`/job/${resp.data.jobs[0]}`);
                                            } else {
                                                setResolveExceptionParams({});
                                            }
                                        });
                                    },
                                }}
                            />
                            <CancelOrderModal
                                open={!!cancelOrderOnRouteModal}
                                handleClose={() => setCancelOrderOnRouteModal(null)}
                                route={route}
                                order={order}
                                refetchOrder={refetchOrder}
                                setNotification={setNotification}
                                isOnRoute={orderCancellable?.isOnRoute}
                                updateOrderStatus={updateOrderStatus}
                            />
                            <CreateReturnModal
                                open={createReturnModalOpen}
                                items={order?.itemsByOrderId || []}
                                callbacks={{
                                    onClose: () => setCreateReturnModalOpen(false),
                                    onSubmit: (selectedItems, clearPoNumber = false) => {
                                        const returnOrder = createNewReturnOrder(selectedItems, clearPoNumber);
                                        submitOrders({
                                            client_id: returnOrder.shipper_id,
                                            orders: [returnOrder],
                                            filename: undefined,
                                            type: 'MANUAL',
                                        }).then(async (resp) => {
                                            if (resp?.data?.jobs?.length > 0) {
                                                // set return_order_id + apply manifest ownership
                                                await saveAssociatedReturnOrder(order_id, resp.data.jobs[0]);
                                                navigate(`/job/${resp.data.jobs[0]}`);
                                            }
                                        });
                                        setCreateReturnModalOpen(false);
                                    },
                                }}
                            />
                        </SecondaryNavContainer>

                        <Grid
                            container
                            css={css`
                                flex-grow: 1;
                            `}
                            className="pb-5"
                        >
                            <Grid item sm={1}></Grid>
                            <Grid item sm={10} className="d-flex justify-content-between align-items-center mt-4">
                                <div
                                    className="d-flex"
                                    css={css`
                                        width: 100%;
                                    `}
                                >
                                    <div
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                            margin-right: 20px;
                                        `}
                                    >
                                        {hasLinkedOrders ? (
                                            <>
                                                <div
                                                    id="linkedOrdersTrigger"
                                                    onClick={(e) => setLinkedAnchorEl(e.currentTarget)}
                                                    aria-controls={linkedMenuOpen ? 'linkedOrdersMenu' : undefined}
                                                    aria-haspopup="true"
                                                    aria-expanded={linkedMenuOpen ? 'true' : undefined}
                                                    css={css`
                                                        display: inline-flex;
                                                        align-items: center;
                                                        align-self: flex-start;
                                                        gap: 4px;
                                                        padding: 2px 12px;
                                                        border-radius: 8px;
                                                        cursor: pointer;
                                                        background-color: ${linkedMenuOpen
                                                            ? colors.greys.border
                                                            : 'transparent'};
                                                        transition: background-color 0.15s ease;
                                                        & .linked-chevron {
                                                            visibility: ${linkedMenuOpen ? 'visible' : 'hidden'};
                                                            font-size: 2rem;
                                                        }
                                                        &:hover {
                                                            background-color: ${colors.greys.border};
                                                        }
                                                        &:hover .linked-chevron {
                                                            visibility: visible;
                                                        }
                                                    `}
                                                >
                                                    <Typography variant="h1">{orderTitle}</Typography>
                                                    <ArrowDropDown className="linked-chevron" />
                                                </div>
                                                <Menu
                                                    id="linkedOrdersMenu"
                                                    open={linkedMenuOpen}
                                                    anchorEl={linkedAnchorEl}
                                                    onClose={() => setLinkedAnchorEl(null)}
                                                    getContentAnchorEl={null}
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                                    PaperProps={{
                                                        style: {
                                                            maxHeight: 360,
                                                            minWidth: 360,
                                                            overflowY: 'auto',
                                                            backgroundColor: colors.greys.selected,
                                                        },
                                                    }}
                                                    MenuListProps={{ 'aria-labelledby': 'linkedOrdersTrigger' }}
                                                >
                                                    {linkedOrders.map((o) => (
                                                        <MenuItem
                                                            key={o.order_id}
                                                            selected={o.order_id === order.order_id}
                                                            onClick={() => {
                                                                setLinkedAnchorEl(null);
                                                                navigate(orderDetailsPath(o.order_id));
                                                            }}
                                                            css={css`
                                                                display: flex;
                                                                justify-content: space-between;
                                                                gap: 32px;
                                                            `}
                                                        >
                                                            <span
                                                                css={css`
                                                                    font-weight: 600;
                                                                `}
                                                            >
                                                                {o.po_number
                                                                    ? `${o.order_number}, ${o.po_number}`
                                                                    : o.order_number}
                                                            </span>
                                                            {o.created_at && (
                                                                <span
                                                                    css={css`
                                                                        color: ${colors.greys.secondary};
                                                                    `}
                                                                >
                                                                    Created:{' '}
                                                                    {format(new Date(o.created_at), 'MMM d, yyyy')}
                                                                </span>
                                                            )}
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            </>
                                        ) : (
                                            <Typography variant="h1">{orderTitle}</Typography>
                                        )}
                                        {(order.references || []).length > 0 && (
                                            <div
                                                css={css`
                                                    display: flex;
                                                    flex-wrap: wrap;
                                                    gap: 12px;
                                                    margin-top: 4px;
                                                `}
                                            >
                                                {order.references.map((ref) => (
                                                    <Typography
                                                        key={ref.reference_id}
                                                        variant="body2"
                                                        css={css`
                                                            color: #6f6f6f;
                                                        `}
                                                    >
                                                        {ref.label || ref.key
                                                            ? `${ref.label || ref.key}: ${ref.value || ''}`
                                                            : ref.value || ''}
                                                    </Typography>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {(order.order_type === 'return' || order.order_type === 'wh_return') && (
                                        <Typography
                                            variant="subtitle2"
                                            css={css`
                                                margin-right: 12px;
                                                color: ${colors.greens.primary};
                                                border-color: ${colors.greens.primary};
                                            `}
                                        >
                                            {order.order_type === 'return' ? 'Regular Return' : 'Warehouse Return'}
                                        </Typography>
                                    )}
                                    <div
                                        css={css`
                                            margin-left: auto;
                                            display: flex;
                                            flex-direction: column;
                                            align-items: flex-end;
                                        `}
                                    >
                                        <Typography variant="subtitle2">
                                            <span
                                                css={css`
                                                    text-wrap-mode: nowrap;
                                                `}
                                            >
                                                {orderStatusStrings[order.order_status]}
                                            </span>
                                            {editable && (
                                                <ArrowDropDown
                                                    id="menuArrow"
                                                    onClick={handleClick}
                                                    aria-controls={openMenu ? 'statusMenu' : undefined}
                                                    aria-expanded={openMenu ? 'true' : undefined}
                                                    aria-haspopup="true"
                                                />
                                            )}
                                        </Typography>
                                        {orderRate && (
                                            <Typography
                                                variant="subtitle2"
                                                css={css`
                                                    margin-top: 8px;
                                                    color: black;
                                                    border: none;
                                                `}
                                            >
                                                Rate: {orderRate}
                                            </Typography>
                                        )}
                                    </div>
                                    <Menu
                                        open={openMenu}
                                        id={'statusMenu'}
                                        onClose={handleClose}
                                        anchorEl={anchorEl}
                                        getContentAnchorEl={null}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'center',
                                        }}
                                        MenuListProps={{
                                            'aria-labelledby': 'menuArrow',
                                        }}
                                    >
                                        <MenuItem
                                            disabled={!editable}
                                            onClick={() => handleSubmitStatus(order.oms ? 'pending' : 'claimed')}
                                        >
                                            {order.oms ? 'Pending' : 'Claimed'}
                                        </MenuItem>
                                        <MenuItem disabled={!editable} onClick={() => handleSubmitStatus('on_hold')}>
                                            On Hold
                                        </MenuItem>
                                        <MenuItem
                                            disabled={
                                                !editable ||
                                                (order.wh_events || []).some((e) => e.action.includes('ADD_CD'))
                                            }
                                            onClick={() => handleSubmitStatus('inProgress')}
                                        >
                                            In Progress
                                        </MenuItem>
                                        <MenuItem disabled={!editable} onClick={() => handleSubmitStatus('complete')}>
                                            Complete
                                        </MenuItem>
                                    </Menu>
                                </div>

                                {isOnwardEngineer ? (
                                    <>
                                        <Autocomplete
                                            value={eventInput}
                                            onChange={(e, value) => setEventInput(value)}
                                            options={[
                                                'globals:price_order',
                                                'carrier_assigned',
                                                'carrier_received',
                                                'cd_in_transit',
                                                'customer_comms_availability',
                                                'customer_comms_predelivery',
                                                'exception_reported',
                                                'item_tracking_events',
                                                'metro:order_ready_for_pickup',
                                                'middle_mile_carrier_assigned',
                                                'middle_mile_return_to_sender',
                                                'mm_arrived',
                                                'mm_delivery_scheduled',
                                                'mm_dispatched',
                                                'mm_in_transit',
                                                'mm_pickup_scheduled',
                                                'mm_cancelled',
                                                'order_rejected',
                                                'order_cancelled',
                                                'order_completed',
                                                'order_created',
                                                'order_details_updated',
                                                'order_dispatched',
                                                'order_in_transit',
                                                'order_incomplete',
                                                'order_pickup_scheduled',
                                                'order_pickup_rescheduled',
                                                'order_pickup_scheduled_timeframe',
                                                'order_pickup_rescheduled_timeframe',
                                                'order_scheduled',
                                                'order_rescheduled',
                                                'order_unscheduled',
                                                'order_scheduled_timeframe',
                                                'order_rescheduled_timeframe',
                                                'customer_contact',
                                                'pod_uploaded',
                                                'delivery_photo_uploaded',
                                                'item_labels_uploaded',
                                                'shipper_rate_updated',
                                                'shipper_invoice_created',
                                                'carrier_invoice_created',
                                            ]}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Event" size="small" />
                                            )}
                                            css={css`
                                                width: 500px;
                                                margin-right: 4px;
                                                align-self: flex-start;
                                            `}
                                        />
                                        <IconButton
                                            css={css`
                                                margin-right: 16px;
                                                color: ${colors.greens.primary};
                                                align-self: flex-start;
                                            `}
                                            onClick={() => {
                                                post(TRIGGER_ORDER_EVENT, {
                                                    order_ids: [order_id],
                                                    event: eventInput,
                                                });
                                            }}
                                        >
                                            <RocketLaunchIcon />
                                        </IconButton>
                                    </>
                                ) : null}

                                {(order.order_status === 'complete' || order.order_status === 'routedComplete') &&
                                    accountType === 'shipper' &&
                                    !isOwnRoute &&
                                    (carrierReview ? (
                                        <Button
                                            variant="contained"
                                            style={{
                                                backgroundColor: '#59B863',
                                                color: 'white',
                                                marginLeft: 'auto',
                                                marginRight: '22px',
                                                paddingLeft: '34px',
                                                paddingRight: '34px',
                                            }}
                                            onClick={() => setCarrierReviewOpen(true)}
                                        >
                                            Edit Carrier Review
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            style={{
                                                backgroundColor: '#59B863',
                                                color: 'white',
                                                marginLeft: 'auto',
                                                marginRight: '22px',
                                                paddingLeft: '34px',
                                                paddingRight: '34px',
                                            }}
                                            onClick={() => setCarrierReviewOpen(true)}
                                        >
                                            Leave Carrier Review
                                        </Button>
                                    ))}
                                {(order.order_status === 'complete' || order.order_status === 'routedComplete') &&
                                    order.order_type !== 'return' && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                alignSelf: 'flex-start',
                                                marginLeft: '16px',
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                disabled={submitInflight}
                                                style={{
                                                    backgroundColor: '#59B863',
                                                    color: 'white',
                                                    marginLeft: 'auto',
                                                    marginRight: '8px',
                                                    height: '36px',
                                                }}
                                                onClick={() => setCreateReturnModalOpen(true)}
                                            >
                                                Create Return
                                            </Button>
                                            <Tooltip title="Create a new return order based on the items and customer information from this order.">
                                                <InfoCircle style={{ marginRight: '22px', color: '#59B863' }} />
                                            </Tooltip>
                                        </div>
                                    )}
                            </Grid>
                            <Grid item sm={1}></Grid>

                            {/* Order Details */}
                            {tabIndex === 0 && (
                                <>
                                    {order?.source_form === 'CUSTOMER' &&
                                        (order.shipper_id === user_id || isOnwardAdmin) &&
                                        !['complete', 'cancelled', 'rejected'].includes(order.order_status) && (
                                            <>
                                                <Grid item sm={1}></Grid>
                                                <Grid container item sm={10} className="mt-4">
                                                    <Grid item>
                                                        <PrimaryButton onClick={() => setEditingItemPieceCount(true)}>
                                                            {order?.piece_count_verified
                                                                ? 'Update Piece Count'
                                                                : 'Verify Piece Count'}
                                                        </PrimaryButton>
                                                    </Grid>
                                                </Grid>
                                                <Grid item sm={1}></Grid>
                                            </>
                                        )}
                                    <Grid item sm={1}></Grid>
                                    <Grid container item sm={10}>
                                        <CarrierReviewModal
                                            isOpen={carrierReviewOpen}
                                            reviewObject={carrierReview}
                                            orderId={order.order_id}
                                            revieweeId={order.carrier_id ? order.carrier_id : order.shipper_id}
                                            revieweeType={order.carrier_id ? 'CARRIER' : 'SHIPPER'}
                                            driverId={route?.driverId}
                                            callbacks={{
                                                onClose: () => setCarrierReviewOpen(false),
                                            }}
                                        />

                                        {/* Only allow user to edit the budget if user is a carrier */}
                                        {accountType === 'carrier' && order.customer_budget ? (
                                            <Grid container className="mb-4 mt-4">
                                                <CarrierEditBudget order={order} />
                                            </Grid>
                                        ) : null}

                                        {(order?.exceptions || [])
                                            .filter((mapping) => mapping.exception_type === 'ORDER')
                                            .map((mapping) => {
                                                return (
                                                    <Grid container className="mt-3">
                                                        <ExceptionCard
                                                            exception={mapping.exception}
                                                            order={order}
                                                            callbacks={{
                                                                setExceptionParams,
                                                                setResolveExceptionParams,
                                                            }}
                                                        />
                                                    </Grid>
                                                );
                                            })}

                                        {itemExceptionGroups.map((group, idx) => {
                                            const key = group?.exception?.exception_id || idx;
                                            return (
                                                <Grid key={key} container className="mt-4">
                                                    <ExceptionCard
                                                        exception={group.exception}
                                                        order={order}
                                                        exceptionItems={group.items}
                                                        callbacks={{
                                                            setExceptionParams,
                                                            setResolveExceptionParams,
                                                        }}
                                                    />
                                                </Grid>
                                            );
                                        })}

                                        <Grid container className="mt-3">
                                            <>
                                                {finalMileDriver ? (
                                                    <Grid item container justifyContent={'flex-start'}>
                                                        <Typography
                                                            className="my-2"
                                                            css={css`
                                                                color: ${colors.greys.primary};
                                                                font-weight: 600;
                                                                font-size: 16px;
                                                            `}
                                                        >
                                                            Driver: {finalMileDriver?.username}{' '}
                                                            {toNational(finalMileDriver?.phone)}
                                                        </Typography>
                                                    </Grid>
                                                ) : null}
                                                {crossDockDriver ? (
                                                    <Grid item container justifyContent={'flex-start'}>
                                                        <Typography
                                                            className="my-2"
                                                            css={css`
                                                                color: ${colors.greys.primary};
                                                                font-weight: 600;
                                                                font-size: 16px;
                                                            `}
                                                        >
                                                            Cross-dock Driver: {crossDockDriver?.username}{' '}
                                                            {toNational(crossDockDriver?.phone)}
                                                        </Typography>
                                                    </Grid>
                                                ) : null}
                                            </>
                                            {order.listing &&
                                                order?.listing.listing_status === 'claimed' &&
                                                order.order_carrier && (
                                                    <OnwardCard
                                                        style={{
                                                            marginTop: '40px',
                                                            marginBottom: '40px',
                                                            backgroundColor: '#59B863',
                                                        }}
                                                    >
                                                        <Grid container spacing={1} alignItems="center">
                                                            <Grid item xs={1}>
                                                                <StoreIcon
                                                                    fontSize="large"
                                                                    style={{ color: 'white' }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={11}>
                                                                <Typography
                                                                    className="my-2"
                                                                    style={{ color: 'white' }}
                                                                    variant="body1"
                                                                >
                                                                    Sent to Onward Marketplace
                                                                </Typography>
                                                                <Typography
                                                                    className="my-2"
                                                                    style={{ color: 'white' }}
                                                                    variant="body2"
                                                                >
                                                                    Order claimed.
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </OnwardCard>
                                                )}

                                            {order?.messages?.some((m) => m.link_type === 'customer_predelivery') &&
                                                isOrderHandler && (
                                                    <CustomerAddressConfirmationCard
                                                        order={order}
                                                        goToCommsTab={() => {
                                                            setTabIndex(3);
                                                        }}
                                                        setNotification={setNotification}
                                                    />
                                                )}

                                            {/* The order's own integration counts too once it has been sent: Vault orders can
                                                come in on a carrier with no dispatch_integration_type, and Sync Order lives on
                                                this card. Unsent, the card would only offer a Send button that can't work. */}
                                            {(accountType === 'carrier' || isOnwardAdmin) &&
                                                (order?.order_carrier?.dispatch_integration_type ||
                                                    (order?.dispatch_integration_type &&
                                                        order?.dispatch_integration_meta) ||
                                                    dispatchTrackCompatibilityVar ||
                                                    xceleratorAxisCompatibilityVar) && (
                                                    <Grid
                                                        container
                                                        className="d-flex justify-content-between align-items-start"
                                                        spacing={4}
                                                        style={{ marginBottom: 0, marginTop: 0 }}
                                                    >
                                                        <Grid item xs={12} style={{ height: '100%' }}>
                                                            <IntegrationPartnerCard
                                                                order={order}
                                                                integrationName={
                                                                    dispatchTrackCompatibilityVar ||
                                                                    xceleratorAxisCompatibilityVar ||
                                                                    order?.order_carrier?.dispatch_integration_type ||
                                                                    order.dispatch_integration_type
                                                                }
                                                                setNotification={setNotification}
                                                                refetchOrder={refetchOrder}
                                                                orderLoading={orderLoading}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )}

                                            {!editable ? (
                                                <CrossdockingCard
                                                    readOnly={true}
                                                    order={order}
                                                    isLoading={eventUpsertLoading}
                                                    warehouse={warehouse}
                                                    events={warehouseEvents?.results || []}
                                                    crossdockDriver={crossDockDriver}
                                                    finalMileDriver={finalMileDriver}
                                                    callbacks={{
                                                        createException: (params) => {
                                                            setExceptionParams(params);
                                                        },
                                                    }}
                                                />
                                            ) : (
                                                <CrossdockingCard
                                                    readOnly={false}
                                                    order={order}
                                                    isLoading={eventUpsertLoading}
                                                    warehouse={warehouse}
                                                    events={warehouseEvents?.results || []}
                                                    crossdockDriver={crossDockDriver}
                                                    finalMileDriver={finalMileDriver}
                                                    callbacks={{
                                                        editCrossdockPickup: (params) => {
                                                            setCrossdockPickupParams(params);
                                                        },
                                                        editCrossdock: (params) => {
                                                            setCrossdockConfigParams(params);
                                                        },
                                                        editPalletDimensions: () => {
                                                            setPalletDimensionsParams(true);
                                                        },
                                                        upsertEvent: ({
                                                            event,
                                                            events,
                                                            order: update,
                                                            trackingEvents,
                                                        }) => {
                                                            let toInsert = (events || (!!event ? [event] : [])).map(
                                                                (e) => {
                                                                    const {
                                                                        __typename,
                                                                        state,
                                                                        transition,
                                                                        driver,
                                                                        wh_driver,
                                                                        location,
                                                                        ...remaining
                                                                    } = e;
                                                                    return remaining;
                                                                }
                                                            );
                                                            return upsertEvent({
                                                                variables: {
                                                                    events: toInsert,
                                                                    order: {
                                                                        ...(update || {}),
                                                                        order_id: order.order_id,
                                                                    },
                                                                    update_cols: Object.keys(toInsert[0] || {}),
                                                                    order_update_cols: Object.keys(update || {}),
                                                                    tracking_events: trackingEvents || [],
                                                                },
                                                            });
                                                        },
                                                        editReceiveEvent: (params) => setReceiveParams(params),
                                                        editScheduled: (params) => setScheduleParams(params),
                                                        editTimeframe: (params) => setTimeframeParams(params),
                                                        editAssigned: (params) => setAssignParams(params),
                                                        confirmOrder: () => {
                                                            const update = {
                                                                delivery_time_confirmed: 'confirmed',
                                                                confirmation_source: CONFIRMATION_SOURCE.MANUAL,
                                                            };
                                                            upsertOrderDynamic({
                                                                variables: {
                                                                    orders: [
                                                                        {
                                                                            order_id: order.order_id,
                                                                            ...update,
                                                                        },
                                                                    ],
                                                                    order_update_cols: Object.keys(update),
                                                                    items: [],
                                                                    removals: [],
                                                                    item_update_cols: [],
                                                                },
                                                            });
                                                        },
                                                        sendConfirmationText: () => {
                                                            post(CROSSDOCK_CONFIRM_TIMEFRAME_TEXT, {
                                                                orderId: order.order_id,
                                                                forceRetry: true,
                                                            })
                                                                .then(() => {
                                                                    setNotification({
                                                                        severity: 'success',
                                                                        message:
                                                                            'Customer texted, awaiting confirmation',
                                                                    });
                                                                })
                                                                .catch((error) => {
                                                                    captureException(error);
                                                                    setNotification({
                                                                        severity: 'error',
                                                                        message:
                                                                            'Failed to send customer timeframe confirmation text',
                                                                    });
                                                                });
                                                        },
                                                        createException: (params) => {
                                                            setExceptionParams(params);
                                                        },
                                                        confirmAction: (content, callback) => {
                                                            setConfirmation({ content, callback });
                                                        },
                                                        openChecklist: (params, callback) => {
                                                            setChecklist({
                                                                ...params,
                                                                callback,
                                                            });
                                                        },
                                                        sendWeblink: (event, driverType) => {
                                                            sendWeblink(order.order_id, event.event_id, driverType);
                                                        },
                                                        gotoFileTab: () => {
                                                            setTabIndex(2);
                                                        },
                                                        openDriverTracking: (driver) => {
                                                            setDriverTracking(driver);
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Grid>

                                        {(accountType === 'carrier' || order.oms) && (
                                            <>{renderNotesCardIfExists('Carrier Notes', order.carrier_notes)}</>
                                        )}

                                        {mmIntegrationType !== 'NONE' ? (
                                            <Grid
                                                container
                                                className="d-flex justify-content-between align-items-start"
                                                spacing={4}
                                                style={{ marginBottom: 0, marginTop: 0 }}
                                            >
                                                <Grid item xs={12} style={{ height: '100%' }}>
                                                    <MiddleMileCard
                                                        order={order}
                                                        isOnwardAdmin={isOnwardAdmin}
                                                        isImposter={isImposter}
                                                        overridesById={overridesById}
                                                        type={mmIntegrationType}
                                                        loading={{ update: upsertDynamicLoading }}
                                                        callbacks={{
                                                            updateOrder: upsertOrderDynamic,
                                                        }}
                                                        refetchOrder={refetchOrder}
                                                        setNotification={setNotification}
                                                        orderLoading={orderLoading}
                                                    />
                                                </Grid>
                                            </Grid>
                                        ) : null}

                                        <Grid
                                            container
                                            spacing={4}
                                            css={css`
                                                margin-top: 0;
                                                margin-bottom: 0;
                                                flex-wrap: nowrap;
                                                justify-content: space-between;
                                            `}
                                        >
                                            {order.order_type !== 'service' ? (
                                                <Grid item css={cardFrag}>
                                                    <PickUpCard
                                                        order={order}
                                                        editable={editable}
                                                        user_id={user_id}
                                                        orderNotes={orderNotes}
                                                    />
                                                </Grid>
                                            ) : null}
                                            {isFtlCrossdocked ? (
                                                <Grid item css={cardFrag}>
                                                    <WHCrossdockCard
                                                        order={order}
                                                        editable={editable}
                                                        user_id={user_id}
                                                    />
                                                </Grid>
                                            ) : null}
                                            {!['will_call', 'wh_inventory'].includes(order.order_type) ? (
                                                <Grid item css={cardFrag}>
                                                    <DropOffCard
                                                        order={order}
                                                        editable={editable}
                                                        user_id={user_id}
                                                        orderNotes={orderNotes}
                                                    />
                                                </Grid>
                                            ) : null}
                                            {order.order_type === 'will_call' ? (
                                                <Grid item css={cardFrag}>
                                                    <WillCallCard order={order} />
                                                </Grid>
                                            ) : null}
                                        </Grid>
                                        <Grid
                                            container
                                            className="d-flex justify-content-between align-items-start"
                                            spacing={4}
                                            style={{ marginBottom: 0, marginTop: 0 }}
                                        >
                                            <Grid item xs={6}>
                                                <OrderSummaryCard
                                                    order={order}
                                                    editable={editable}
                                                    userType={accountType}
                                                    editPalletDimensions={() => {
                                                        setPalletDimensionsParams(true);
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item container xs={6} direction="column">
                                                <Grid item style={{ marginBottom: '16px' }}>
                                                    <Grid container>
                                                        <WarehousingCard
                                                            warehouse={warehouse}
                                                            order={order}
                                                            orderNotes={orderNotes}
                                                            warehouseEditable={warehouseEditable}
                                                            editable={editable}
                                                            manifestEditable={manifestEditable}
                                                            refetchOrder={refetchOrder}
                                                            setNotification={setNotification}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid item style={{ marginTop: '16px' }}>
                                                    {order?.carrier_id ? (
                                                        <Grid container>
                                                            <CarrierInfoCard
                                                                order={order}
                                                                carrier={order?.order_carrier}
                                                                user_id={user_id}
                                                            />
                                                        </Grid>
                                                    ) : (
                                                        <EmptyCard
                                                            title={'Carrier Info'}
                                                            text={
                                                                'Carrier info displays when orders are claimed from the Onward Marketplace. You may send orders to the marketplace from the Unassigned page, or on order creation. If you do not have access to this feature, reach out to Onward admins.'
                                                            }
                                                        />
                                                    )}
                                                </Grid>
                                                {Object.entries(surveyResponses).map(([surveyType, responses]) => (
                                                    <Grid
                                                        key={surveyType}
                                                        item
                                                        css={css`
                                                            margin-top: 16px;
                                                        `}
                                                    >
                                                        <SurveyCard
                                                            surveyType={surveyType}
                                                            responses={responses}
                                                            order={order}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Grid>

                                        {order?.routes?.length > 0 && editable && (
                                            <Grid container className="mt-2">
                                                <RoutedOrderCard order={order} editable={editable} />
                                            </Grid>
                                        )}
                                    </Grid>
                                    <Grid item sm={1}></Grid>
                                </>
                            )}

                            {tabIndex === 1 && (
                                <>
                                    <OrderItems
                                        order={order}
                                        callbacks={{
                                            setExceptionParams,
                                            setResolveExceptionParams,
                                            setMapParams,
                                            setNotification,
                                        }}
                                        resolutionRemovedItems={resolutionRemovedItems}
                                    />
                                    <MapModal
                                        {...mapParams}
                                        callbacks={{
                                            onClose: () => setMapParams({}),
                                        }}
                                    />
                                </>
                            )}

                            {tabIndex === 2 && (
                                <OrderImages
                                    userType={accountType}
                                    editable={editable}
                                    onImgView={setViewImg}
                                    order={order}
                                />
                            )}

                            {tabIndex === 3 && (
                                <>
                                    <Grid item xs={1} />
                                    <Grid
                                        item
                                        xs={10}
                                        css={css`
                                            height: 100%;
                                            margin-top: 3rem;
                                        `}
                                    >
                                        <Grid container>
                                            <CommunicationLog
                                                orderNumber={order.order_number}
                                                customerName={order.dropoff_name}
                                                customerPhone={order.dropoff_phone}
                                                customerZip={order.dropoff_zip}
                                                order={order}
                                                order_id={order.order_id}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={1} />
                                </>
                            )}

                            {tabIndex === 4 && (
                                <Notes privacyExp={privacyExp} notes={notes} order={order} editable={editable} />
                            )}

                            {tabIndex === 5 &&
                                (user?.roles?.['ADMIN'] || user?.roles?.['ONWARD_ADMIN'] || teammateRoles?.admin) && (
                                    <>
                                        <Grid item xs={1} />
                                        <Grid
                                            item
                                            xs={10}
                                            css={css`
                                                height: 100%;
                                                margin-top: 1rem;
                                            `}
                                        >
                                            <RatesTab order={order} refetchOrder={refetchOrder} />
                                        </Grid>
                                        <Grid item xs={1} />
                                    </>
                                )}

                            {tabIndex === 7 && (
                                <>
                                    <Grid item sm={1}></Grid>
                                    <Grid
                                        container
                                        item
                                        sm={10}
                                        css={css`
                                            height: 100%;
                                            margin-top: 1rem;
                                        `}
                                    >
                                        <OnwardCard>
                                            <OrderServicesTab
                                                order={order}
                                                serviceCatalog={serviceCatalog}
                                                isCarrierAdmin={isCarrierAdmin}
                                                onNotify={setNotification}
                                            />
                                        </OnwardCard>
                                    </Grid>
                                    <Grid item sm={1}></Grid>
                                </>
                            )}

                            {tabIndex === 6 && (
                                <>
                                    <Grid item xs={1} />
                                    <Grid
                                        item
                                        xs={10}
                                        css={css`
                                            height: 100%;
                                            margin-top: 3rem;
                                        `}
                                    >
                                        <Grid
                                            container
                                            direction="column"
                                            css={css`
                                                height: 100%;
                                                padding-bottom: 2rem;
                                            `}
                                        >
                                            <Grid
                                                container
                                                css={css`
                                                    flex-grow: 1;
                                                `}
                                            >
                                                <ActivityLog
                                                    order_id={order.order_id}
                                                    editable={editable}
                                                    tz={
                                                        zipcode_to_timezone.lookup(order?.[genAttributes(order).zip]) ||
                                                        'America/New_York'
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={1} />
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Box>
                {viewImg && <Lightbox large={viewImg} hideZoom={true} onClose={() => setViewImg('')} />}
            </ThemeProvider>

            {/* Modal */}
            <AssignedToRouteErrorModal
                open={!!assignedToRouteErrorModal}
                onClose={(e) => setAssignedToRouteErrorModal(false)}
            />
            <RescheduleOrderModal
                open={rescheduleOpen}
                onClose={() => setRescheduleOpen(false)}
                onConfirm={handleReschedule}
                loading={eventUpsertLoading || insertExceptionEventLoading || releaseFromRouteLoading}
                order={order}
            />
            <ExceptionModal
                {...exceptionParams}
                loading={insertExceptionEventLoading || insertExceptionLoading || updateExceptionLoading}
                order={order}
                callbacks={{
                    onError: (e) => {
                        setNotification({
                            severity: 'error',
                            message: 'Failed to upload image. Please try again',
                        });
                    },
                    onSubmitCreateAnother: ({ exception }) => {
                        return createException(exception);
                    },
                    onSubmit: ({ exception }) => {
                        return createException(exception).then(() => {
                            setExceptionParams(null);
                        });
                    },
                    onClose: () => setExceptionParams(null),
                }}
            />

            {/* Notification */}
            <Snackbar
                open={!!notification?.message}
                onClose={() => setNotification(null)}
                anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                autoHideDuration={10000}
            >
                <Alert
                    onClose={() => setNotification(null)}
                    severity={notification?.severity}
                    elevation={6}
                    variant="filled"
                >
                    {notification?.message}
                    {notification?.clickable && notification.clickable}
                </Alert>
            </Snackbar>
        </>
    );
}

export default OrderDetailsPage;
